import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import PopoutWindow from '../common/PopoutWindow';
import { openFilePreview } from '../common/FilePreviewModal';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import Select2Dropdown from '../common/Select2Dropdown';
import OtherPayablesForm from '../admin/OtherPayablesForm';
import ShippingLinePayablesForm from '../admin/ShippingLinePayablesForm';
import PayableExpensesView from '../admin/PayableExpensesView';
import ConfirmDialog from '../common/ConfirmDialog';
import EmailModal from '../common/EmailModal';
import { sendAndSaveStatementEmail } from '../../services/statementEmailService';
import RunningStatement from './RunningStatement';
import * as XLSX from 'xlsx';

const API_BASE = '/api/documentation/statements';
const STATEMENT_CHARGE_NAMES_API = '/api/masters/statement-charge-names/';
const API_PAYABLE_PERMS = '/api/admin/payable-user-permissions/my-permissions/';
const API_OTHER_PAYABLE = '/api/admin/other-payables/';
const API_SHIPPING_LINE_PAYABLE = '/api/admin/shipping-line-payables/';
const API_INVOICE_PREVIEW = '/api/documentation/jobs/invoice-preview/';

const todayISO = () => new Date().toISOString().split('T')[0];

const formatCurrency = (n) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const parseAmount = (value) => {
  const v = parseFloat(String(value || '0').replace(/,/g, ''));
  return Number.isNaN(v) ? 0 : v;
};

const roundToThousand = (amount) => {
  const n = Math.round(amount || 0);
  const lower = Math.floor(n / 1000) * 1000;
  const rem = n - lower;
  return rem <= 100 ? lower : lower + 1000;
};

const toDisplay = (v) => (v === null || v === undefined || v === '' ? '-' : String(v).toUpperCase());

const formatDate = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const iso = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return v;
};

const formatDateTimeDisplay = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const d = new Date(v);
  if (!isNaN(d)) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  const iso = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!iso) return v;
  const date = `${iso[3]}/${iso[2]}/${iso[1]}`;
  const time = String(v).match(/(\d{2}:\d{2})/);
  return time ? `${date} ${time[1]}` : date;
};

const statementEmailAppendHtml = (ctx, bank) => {
  const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const c = ctx || {};
  let html = '';
  const details = [];
  if (c.supplier_name) details.push(`<p><strong>Supplier Name:</strong> ${esc(c.supplier_name)}</p>`);
  if (c.invoice_no) details.push(`<p><strong>INV No:</strong> ${esc(c.invoice_no)}</p>`);
  if (c.bl_no) details.push(`<p><strong>BL No:</strong> ${esc(c.bl_no)}</p>`);
  if (c.balance_display) details.push(`<p><strong>Outstanding Amount:</strong> ${esc(c.balance_display)}</p>`);
  if (details.length) html += `<br>${details.join('')}`;
  if (c.balance_display) html += '<p>Kindly clear your outstanding amount.</p>';
  if (c.balance_display && bank) {
    if (bank.id === 'CASH') {
      html += '<p>Requesting you to make arrangements to clear the outstanding by depositing in CASH.</p>';
    } else {
      const bankName = bank.bank_name || bank.short_name || bank.name || '';
      const acctNo = bank.account_number || bank.account_no || '';
      const currency = bank.currency || bank.account_currency || '';
      const branch = bank.branch || bank.branch_name || '';
      const company = c.company_name || '';
      html += `<p>Requesting you to make arrangements to clear the outstanding by depositing in our <strong><u>${esc(bankName)}</u></strong> account.</p>`;
      html += `<p>Please find bank details as per below and share proof of payment once you have deposited the payment in our account:<br><br><strong>${esc(company)}</strong><br><strong>${esc(bankName)}</strong><br>ACCOUNT NUMBER ${esc(currency)}: ${esc(acctNo)}<br>${esc(branch)} BRANCH</p>`;
    }
  }
  const fullName = `${localStorage.getItem('kk_first_name') || ''} ${localStorage.getItem('kk_last_name') || ''}`.trim()
    || localStorage.getItem('kk_user') || '';
  let signatureBlock;
  if (c.signature_image) {
    signatureBlock = `<img data-staff-signature="1" src="${esc(c.signature_image)}" alt="Signature" style="max-height: 120px;" />`;
  } else if (c.email_signature && String(c.email_signature).trim()) {
    signatureBlock = String(c.email_signature);
  } else {
    signatureBlock = `<p>${esc(fullName)}<br>P.O.Box 22208<br>Dar es Salaam, Tanzania<br>Tel: 0782 978978 / 0713 111110<br>Email: zkhakoo@kkcompany.co.tz</p>`;
  }
  html += `<br><br>Thank you,<br><br>${signatureBlock}<br><p style="font-size: small; color: #888;"><strong>This is an automatically generated email. Please do not reply if you have received this email by mistake. In case of any queries, please contact us for further assistance.</strong></p>`;
  return html;
};

const getStatementFileName = (row) => {
  if (row.statement_file_url) {
    try {
      const { pathname } = new URL(row.statement_file_url);
      return decodeURIComponent(pathname.split('/').pop() || '');
    } catch (e) {
      return row.statement_file_url;
    }
  }
  return '-';
};

const cellStyle = {
  border: '1px solid #cbd5e1',
  padding: '6px',
  whiteSpace: 'nowrap',
};

const headerCellStyle = {
  ...cellStyle,
  background: '#e2e8f0',
  fontWeight: 600,
  textAlign: 'left',
};

const StatementPreview = ({ statementId, fileName }) => {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/${statementId}/pdf-preview/`, { headers: getAuthHeaders(false) });
        if (!res.ok) throw new Error(`Failed to load ${fileName}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not preview statement');
      }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [statementId, fileName]);

  if (error) {
    return <div style={{ color: '#dc2626' }}>{error}</div>;
  }
  if (!url) {
    return <div style={{ color: '#64748b' }}>Loading preview…</div>;
  }
  return (
    <iframe
      title="Statement Preview"
      src={url}
      style={{ width: '100%', height: 600, border: '1px solid #cbd5e1', background: '#fff' }}
    />
  );
};

const StatementTopFields = ({ data }) => (
  <div style={{ marginBottom: 16, fontSize: '0.9rem', display: 'flex' }}>
    <div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>IDF:</strong>
      <span>{toDisplay(data.idf_no)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>BL:</strong>
      <span>{toDisplay(data.bl)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>KK FILE NO:</strong>
      <span>{toDisplay(data.kk_file_no)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>STATEMENT NO:</strong>
      <span>{toDisplay(data.statement_no)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>IMPORTER:</strong>
      <span>{toDisplay(data.customer_name)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>SUPPLIER:</strong>
      <span>{toDisplay(data.supplier_name)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>CLIENT REF NO:</strong>
      <span>{toDisplay(data.client_ref_no)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>SHIPMENT TYPE:</strong>
      <span>{toDisplay(data.shipment_type)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>CARGO TYPE:</strong>
      <span>{toDisplay(data.cargo_type)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>TRANSPORTER:</strong>
      <span>{/(LCL|\bAIR\b)/i.test(`${data.cargo_type || ''} ${data.shipment_type || ''}`) ? '' : toDisplay(data.transporter)}</span>
    </div>
    <div style={{ display: 'flex', marginBottom: 4 }}>
      <strong style={{ minWidth: 160 }}>RELEASE DATE:</strong>
      <span>{formatDate(data.release_date)}</span>
    </div>
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', fontWeight: 'bold' }}>
      {String(data.cargo_type || '').toUpperCase().includes('FCL') && (
        <div>DROPOFF LOCATION:&nbsp;<span style={{ color: '#dc2626' }}>{toDisplay(data.drop_off_location)}</span></div>
      )}
      {data.payables_pending && (
        <div style={{ color: '#dc2626', fontWeight: 'bold', textAlign: 'center', marginTop: 6, animation: 'cs-flash 1s infinite' }}>
          PAYABLES PENDING APPROVAL
        </div>
      )}
    </div>
  </div>
);

const formatBalanceCell = (v) => (v > 0
  ? `${formatCurrency(v)} DR`
  : v < 0
    ? `(${formatCurrency(Math.abs(v))}) CR`
    : '0.00');

// Renders the DR/CR/BALANCE cells for one client row in the Generated tab.
const ClientLedgerSummaryCells = ({ customerName, summary: summaryProp = null }) => {
  const [fetchedSummary, setFetchedSummary] = useState(null);
  const summary = summaryProp || fetchedSummary;

  useEffect(() => {
    if (summaryProp) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/customer-ledger-summary/?customer=${encodeURIComponent(customerName)}`,
          { headers: getAuthHeaders(false) }
        );
        if (res.ok && !cancelled) setFetchedSummary(await res.json());
      } catch { /* summary stays null */ }
    };
    load();
    return () => { cancelled = true; };
  }, [customerName]);

  const numCell = { ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' };

  if (!summary) {
    return <>{Array.from({ length: 6 }).map((_, i) => <td key={i} style={numCell}>-</td>)}</>;
  }

  const balanceTzs = summary.balance_tzs || 0;
  const balanceUsd = summary.balance_usd || 0;

  return (
    <>
      <td style={numCell}>{formatCurrency(summary.dr_tzs)}</td>
      <td style={numCell}>{formatCurrency(summary.cr_tzs)}</td>
      <td
        style={{ ...numCell, fontWeight: 700, color: balanceTzs > 0 ? '#c62828' : balanceTzs < 0 ? '#16a34a' : '#64748b' }}
        className={balanceTzs > 0 ? 'balance-dr-flash' : ''}
      >
        {formatBalanceCell(balanceTzs)}
      </td>
      <td style={numCell}>{formatCurrency(summary.dr_usd)}</td>
      <td style={numCell}>{formatCurrency(summary.cr_usd)}</td>
      <td style={{ ...numCell, fontWeight: 700, color: balanceUsd > 0 ? '#c62828' : balanceUsd < 0 ? '#16a34a' : '#64748b' }}>
        {formatBalanceCell(balanceUsd)}
      </td>
    </>
  );
};

// Self-contained Generate/Edit Debit Note form — each popout window gets its own instance,
// so multiple windows hold independent form state (like Payables' per-window forms).
const GenerateStatementForm = ({ job, editRow = null, excludeStatementId = null, canDelete = false, onSaved, onOpenExpenses }) => {
  const editingStatementId = editRow ? editRow.id : null;
  const statementDate = (editRow && editRow.statement_date) || todayISO();
  const previousStatementId = excludeStatementId;

  const [charges, setCharges] = useState([]);
  const [clientPaid, setClientPaid] = useState([]);
  const [statementChargeNames, setStatementChargeNames] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [expandedClientPaidGroups, setExpandedClientPaidGroups] = useState(new Set());
  const [editingStatementIdx, setEditingStatementIdx] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '' });
  const confirmActionRef = useRef(null);
  const previewUrlRef = useRef(null);
  const dragSrc = useRef(null);
  const dragAfter = useRef(false);

  const paidRows = useMemo(() =>
    clientPaid.filter((cp) =>
      cp.charge_name &&
      ((cp.tzs_amount !== '' && cp.tzs_amount != null) || (cp.usd_amount !== '' && cp.usd_amount != null))
    ),
    [clientPaid]
  );

  const clientPaidGrouped = useMemo(() => {
    const groups = new Map();
    paidRows.forEach((cp) => {
      const g = cp.group || 'OTHER';
      if (!groups.has(g)) groups.set(g, { group: g, items: [] });
      groups.get(g).items.push(cp);
    });
    return Array.from(groups.values());
  }, [paidRows]);

  const totalStatement = useMemo(
    () => charges
      .filter((c) => !c.is_child)
      .reduce((sum, c) => sum + (parseAmount(c.statement_amount) || 0), 0),
    [charges]
  );

  const hasUsdCharges = useMemo(
    () => charges.some((c) => (c.currency || '').toUpperCase() === 'USD'),
    [charges]
  );

  const missingWarnings = useMemo(() => {
    if (!job || !charges.length) return [];
    const shipmentType = (job.shipment_type || '').toUpperCase();
    const isSea = shipmentType === 'IMPORT-SEA';
    const isAir = shipmentType === 'IMPORT-AIR';
    if (!isSea && !isAir) return [];

    const hasVerification = charges.some(
      (c) => (c.charge_name || '').toUpperCase().includes('VERIFICATION') || (c.statement_charge_name || '').toUpperCase().includes('VERIFICATION')
    );
    const hasShippingLine = charges.some((c) => {
      const g = (c.group || '').toUpperCase();
      const n = (c.charge_name || '').toUpperCase();
      const v = (c.vendor_type || '').toUpperCase();
      return g.includes('SHIPPING LINE') || n.includes('SHIPPING LINE') || v.includes('SHIPPING LINE');
    });
    const hasIcd = charges.some((c) => {
      const g = (c.group || '').toUpperCase();
      const n = (c.charge_name || '').toUpperCase();
      const v = (c.vendor_type || '').toUpperCase();
      return g.includes('ICD') || n.includes('ICD') || v.includes('ICD');
    });
    const hasAirport = charges.some((c) => {
      const g = (c.group || '').toUpperCase();
      const n = (c.charge_name || '').toUpperCase();
      const v = (c.vendor_type || '').toUpperCase();
      return g.includes('AIRPORT') || n.includes('AIRPORT') || v.includes('AIRPORT');
    });

    const warnings = [];
    if (!hasVerification) warnings.push('VERIFICATION CHARGES MISSING');
    if (isSea) {
      if (!hasShippingLine) warnings.push('SHIPPING LINE CHARGES MISSING');
      if (!hasIcd) warnings.push('ICD CHARGES MISSING');
    }
    if (isAir && !hasAirport) warnings.push('AIRPORT CHARGES MISSING');
    return warnings;
  }, [charges, job]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [chargesRes, namesRes] = await Promise.all([
          fetch(`${API_BASE}/job-charges/?job_id=${encodeURIComponent(job.id)}${excludeStatementId ? `&exclude_statement_id=${encodeURIComponent(excludeStatementId)}` : ''}`, { headers: getAuthHeaders(false) }),
          fetch(STATEMENT_CHARGE_NAMES_API, { headers: getAuthHeaders(false) }),
        ]);
        if (!chargesRes.ok) throw new Error(editRow ? 'Failed to load statement' : 'Failed to load job charges');
        const data = await chargesRes.json();
        const namesData = await namesRes.json().catch(() => []);
        if (cancelled) return;
        setStatementChargeNames(Array.isArray(namesData) ? namesData : []);
        setClientPaid(data.client_paid || []);

        if (editRow) {
          setCharges((editRow.items || []).map((it) => ({
            id: it.id,
            charge_name: it.charge_name,
            statement_charge_name: it.statement_charge_name || '',
            group: it.group || '',
            amount: parseAmount(it.amount),
            rounded: it.rounded,
            statement_amount: parseAmount(it.statement_amount),
            payable_id: it.payable_id,
            payable_type: it.payable_type,
            is_group_header: !!it.group && it.charge_name === it.group,
            is_child: false,
            is_extra: !it.payable_type && !it.payable_id && !it.group,
            currency: it.currency || 'TZS',
          })));
          return;
        }

        const groupPriority = (group) => {
          if (!group) return 99;
          if (group.includes('SHIPPING LINE CHARGES')) return 1;
          if (group.includes('DETENTION')) return 2;
          if (group.includes('ICD')) return 3;
          if (group.includes('BANK CHARGES')) return 4;
          if (group.includes('ADDITIONAL INVOICE')) return 101;
          if (group.includes('CNF CHARGES')) return 100;
          return 5;
        };

        const groupTotals = {};
        (data.charges || []).forEach((c) => {
          const g = c.group || '';
          if (g) groupTotals[g] = (groupTotals[g] || 0) + parseAmount(c.amount);
        });

        const indexed = (data.charges || []).map((c, i) => ({
          ...c,
          _idx: i,
          amount: parseAmount(c.amount),
          statement_amount: roundToThousand(parseAmount(c.amount)),
          rounded: true,
          statement_charge_name: '',
          is_group_header: false,
          is_child: !!c.group,
          currency: c.currency || 'TZS',
        }));

        indexed.sort((a, b) => {
          const pa = groupPriority(a.group);
          const pb = groupPriority(b.group);
          if (pa !== pb) return pa - pb;
          if (a.group !== b.group) return (a.group || '').localeCompare(b.group || '');
          return a._idx - b._idx;
        });

        const result = [];
        let lastGroup = null;
        indexed.forEach((c) => {
          const g = c.group || '';
          if (g && g !== lastGroup) {
            const groupTotal = groupTotals[g] || 0;
            result.push({
              id: `GROUP-${g}`,
              charge_name: g,
              statement_charge_name: '',
              group: g,
              amount: groupTotal,
              statement_amount: roundToThousand(groupTotal),
              rounded: true,
              is_group_header: true,
              is_child: false,
              is_extra: false,
              payable_type: '',
              currency: 'TZS',
            });
            lastGroup = g;
          }
          const { _idx, ...rest } = c;
          result.push(rest);
        });
        setCharges(result);
      } catch (e) {
        if (cancelled) return;
        toast.error(e.message || (editRow ? 'Failed to load statement' : 'Failed to load job charges'));
        setStatementChargeNames([]);
        setClientPaid([]);
        setCharges([]);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = (group) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const toggleClientPaidGroup = (group) => {
    setExpandedClientPaidGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const removeFromGroup = (idx) => {
    setCharges((prev) => {
      const next = [...prev];
      const child = next[idx];
      if (!child || !child.is_child || !child.group) return prev;
      const group = child.group;
      // mark the charge as ungrouped and remove it from the current position
      const ungrouped = { ...child, is_child: false, is_group_header: false, group: '' };
      next.splice(idx, 1);
      // find the last item still in this group (header or remaining children)
      let lastGroupIndex = -1;
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].group === group && (next[i].is_child || next[i].is_group_header)) {
          lastGroupIndex = i;
          break;
        }
      }
      const insertAt = lastGroupIndex >= 0 ? lastGroupIndex + 1 : next.length;
      next.splice(insertAt, 0, ungrouped);
      // recalculate the parent group total
      const newHeaderIdx = next.findIndex((c) => c.is_group_header && c.group === group);
      const remaining = next.reduce(
        (sum, c) => (c.is_child && c.group === group ? sum + parseAmount(c.amount) : sum),
        0
      );
      if (newHeaderIdx >= 0) {
        const header = next[newHeaderIdx];
        next[newHeaderIdx] = {
          ...header,
          amount: remaining,
          statement_amount: roundToThousand(remaining),
        };
      }
      return next;
    });
  };

  const toggleRoundOff = (idx) => {
    setCharges((prev) => {
      const next = [...prev];
      const item = next[idx];
      if (item.is_child) return prev;
      const rounded = !item.rounded;
      next[idx] = {
        ...item,
        rounded,
        statement_amount: rounded ? roundToThousand(parseAmount(item.amount)) : parseAmount(item.amount),
      };
      return next;
    });
  };

  const zeroExtraAmount = (idx) => {
    setCharges((prev) => {
      const next = [...prev];
      const item = next[idx];
      if (!item || item.is_child || !item.is_extra) return prev;
      next[idx] = { ...item, amount: 0, actual_zeroed: true };
      return next;
    });
  };

  const changeStatementAmount = (idx, value) => {
    setCharges((prev) => {
      const next = [...prev];
      const item = next[idx];
      if (item.is_child) return prev;
      const parsed = parseAmount(value);
      next[idx] = { ...item, statement_amount: parsed, ...(item.is_extra && !item.actual_zeroed ? { amount: parsed } : {}) };
      return next;
    });
  };

  const roundExtraStatementAmount = (idx) => {
    setCharges((prev) => {
      const next = [...prev];
      const item = next[idx];
      if (!item || item.is_child || !item.is_extra || !item.rounded) return prev;
      next[idx] = { ...item, statement_amount: roundToThousand(item.statement_amount) };
      return next;
    });
  };

  const changeStatementChargeName = (idx, value) => {
    setCharges((prev) => {
      const next = [...prev];
      const item = next[idx];
      if (item.is_child) return prev;
      next[idx] = { ...item, statement_charge_name: value || '', charge_name: item.is_extra ? (value || '') : item.charge_name };
      return next;
    });
  };

  const addExtraRow = () => {
    const newRow = {
      id: `EXTRA-${Date.now()}`,
      charge_name: '',
      statement_charge_name: '',
      amount: 0,
      statement_amount: 0,
      rounded: true,
      group: '',
      is_group_header: false,
      is_child: false,
      is_extra: true,
      payable_type: '',
      currency: 'TZS',
    };
    setCharges((prev) => {
      const insertIdx = prev.findIndex((c) =>
        (c.group || '').toUpperCase() === 'CNF CHARGES' ||
        (c.charge_name || '').toUpperCase() === 'CNF CHARGES'
      );
      if (insertIdx >= 0) {
        return [...prev.slice(0, insertIdx), newRow, ...prev.slice(insertIdx)];
      }
      return [...prev, newRow];
    });
  };

  const removeExtraRow = (id) => {
    setCharges((prev) => prev.filter((c) => c.id !== id));
  };

  const reorderCharges = (fromIdx, toIdx, after) => {
    if (fromIdx == null || fromIdx === toIdx) return;
    if (charges[fromIdx].is_child || charges[toIdx].is_child) return;
    let fromEnd = fromIdx + 1;
    while (fromEnd < charges.length && charges[fromEnd].is_child) fromEnd++;
    let toEnd = toIdx + 1;
    while (toEnd < charges.length && charges[toEnd].is_child) toEnd++;
    const size = fromEnd - fromIdx;
    const block = charges.slice(fromIdx, fromEnd);
    const next = [...charges];
    next.splice(fromIdx, size);
    let insertAt = toIdx;
    if (fromIdx < toIdx) {
      insertAt = after ? toEnd - size : toIdx - size;
    } else {
      insertAt = after ? toEnd : toIdx;
    }
    next.splice(insertAt, 0, ...block);
    setCharges(next);
  };

  const handleGenerate = async (allowPendingOverride = false) => {
    if (!job || !charges.length) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE}/generate/`, {
        method: 'POST',
        headers: getAuthHeaders(false),
        body: JSON.stringify({
          job_id: job.id,
          statement_date: statementDate,
          previous_statement_id: previousStatementId,
          statement_id: editingStatementId,
          allow_pending_payables: allowPendingOverride === true,
          items: charges
            .filter((c) => !c.is_child)
            .map((c) => ({
              charge_name: c.is_extra ? (c.statement_charge_name || c.charge_name) : c.charge_name,
              statement_charge_name: c.statement_charge_name || '',
              group: c.group || '',
              amount: c.amount,
              rounded: c.rounded,
              statement_amount: c.statement_amount,
              currency: c.currency || 'TZS',
              id: c.id,
              payable_id: c.payable_id,
              payable_type: c.payable_type,
            })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.payables_pending) {
          if (canDelete) {
            // Delete-permission users may acknowledge and continue saving
            confirmActionRef.current = () => handleGenerate(true);
            setConfirmDialog({
              open: true,
              title: 'Payables Pending Approval',
              message: 'There are payables pending approval for this job. Do you want to continue saving the debit note?',
            });
          } else {
            setConfirmDialog({
              open: true,
              title: 'Payables Pending Approval',
              message: 'There are payables pending approval for this job. The Debit Note cannot be saved.',
              okOnly: true,
            });
          }
          return;
        }
        throw new Error(data.detail || 'Failed to generate statement');
      }
      toast.success('Statement saved');
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Failed to save statement');
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    if (!job || !charges.length) return;
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/preview/`, {
        method: 'POST',
        headers: getAuthHeaders(false),
        body: JSON.stringify({
          job_id: job.id,
          statement_date: statementDate,
          previous_statement_id: previousStatementId,
          items: charges
            .filter((c) => !c.is_child)
            .map((c) => ({
              charge_name: c.is_extra ? (c.statement_charge_name || c.charge_name) : c.charge_name,
              statement_charge_name: c.statement_charge_name || '',
              group: c.group || '',
              amount: c.amount,
              rounded: c.rounded,
              statement_amount: c.statement_amount,
              currency: c.currency || 'TZS',
              id: c.id,
              payable_id: c.payable_id,
              payable_type: c.payable_type,
            })),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load preview');
      }
      const blob = await res.blob();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const blobUrl = URL.createObjectURL(blob);
      previewUrlRef.current = blobUrl;
      setPreviewUrl(blobUrl);
    } catch (e) {
      toast.error(e.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const promptGenerate = () => {
    if (!job || !charges.length) return;
    confirmActionRef.current = handleGenerate;
    if (missingWarnings.length > 0) {
      setConfirmDialog({
        open: true,
        title: 'Missing Charges',
        message: (
          <div>
            <div style={{ marginBottom: 8 }}>The following charges are missing:</div>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#dc2626' }}>
              {missingWarnings.map((w) => (
                <li key={w} style={{ marginBottom: 4 }}>{w}</li>
              ))}
            </ul>
            <div style={{ marginTop: 12 }}>Are you sure you would like to save this statement?</div>
          </div>
        ),
      });
    } else {
      setConfirmDialog({
        open: true,
        title: 'Confirm Save',
        message: 'Are you sure would like to save this statement?',
      });
    }
  };

  const handleConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '' });
    if (confirmActionRef.current) {
      confirmActionRef.current();
      confirmActionRef.current = null;
    }
  };

  return (
    <>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{editingStatementId ? 'Edit Statement' : 'Generate Debit Note'}</h3>
          <button
            type="button"
            onClick={() => onOpenExpenses(job)}
            style={{
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              padding: '6px 14px',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            View Expenses
          </button>
        </div>
        <StatementTopFields data={job} />

        <style>{`@keyframes cs-flash { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }`}</style>
        {missingWarnings.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {missingWarnings.map((w) => (
              <div
                key={w}
                style={{
                  color: '#dc2626',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: 6,
                  animation: 'cs-flash 1s infinite',
                }}
              >
                {w}
              </div>
            ))}
          </div>
        )}

        {charges.length === 0 ? (
          <div style={{ color: '#94a3b8' }}>No charges found for this job.</div>
        ) : (
          <>
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
              <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%', position: 'relative', zIndex: 100 }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Charge Name</th>
                  <th style={headerCellStyle}>Statement Charge Name</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amount (TZS)</th>
                  {hasUsdCharges && (
                    <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amount (USD)</th>
                  )}
                  <th style={{ ...headerCellStyle, textAlign: 'center' }}>Round Off</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Statement Amount</th>
                  <th style={{ ...headerCellStyle, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {charges.map((c, idx) => {
                  if (c.is_child) {
                    if (!expandedGroups.has(c.group)) return null;
                    return (
                      <tr key={idx} style={{ background: '#f8fafc' }}>
                        <td style={{ ...cellStyle, paddingLeft: 40, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          <span
                            onClick={() => removeFromGroup(idx)}
                            style={{ color: '#dc2626', cursor: 'pointer', marginRight: 8, fontSize: 16, fontWeight: 'bold' }}
                            title="Remove from group"
                          >-</span>
                          {toDisplay(c.charge_name)}
                        </td>
                        <td style={cellStyle} />
                        <td style={{ ...cellStyle, textAlign: 'right' }}>{c.currency === 'USD' ? '-' : formatCurrency(c.amount)}</td>
                        {hasUsdCharges && (
                          <td style={{ ...cellStyle, textAlign: 'right' }}>{c.currency === 'USD' ? formatCurrency(c.amount) : '-'}</td>
                        )}
                        <td style={cellStyle} />
                        <td style={cellStyle} />
                        <td style={cellStyle} />
                      </tr>
                    );
                  }
                  const stmtDiff = (parseAmount(c.statement_amount) || 0) - (parseAmount(c.amount) || 0);
                  return (
                    <tr
                      key={idx}
                      onDragOver={(e) => {
                        if (dragSrc.current === null) return;
                        e.preventDefault();
                        const rect = e.currentTarget.getBoundingClientRect();
                        dragAfter.current = e.clientY > rect.top + rect.height / 2;
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragSrc.current === null || dragSrc.current === idx) return;
                        reorderCharges(dragSrc.current, idx, dragAfter.current);
                        dragSrc.current = null;
                      }}
                    >
                      <td style={{ ...cellStyle, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {c.is_group_header && (
                            <button
                              type="button"
                              onClick={() => toggleExpand(c.group)}
                              style={{ marginRight: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#dc2626' }}
                            >
                              {expandedGroups.has(c.group) ? '▼' : '▶'}
                            </button>
                          )}
                          {c.is_extra ? <span style={{ color: '#999' }}>Extra charge</span> : toDisplay(c.charge_name)}
                        </div>
                      </td>
                      <td style={cellStyle}>
                        {(c.is_group_header || c.is_extra || !c.is_child) && (
                          <Select2Dropdown
                            options={statementChargeNames.map((n) => ({ id: n.name, text: n.name }))}
                            value={c.statement_charge_name || ''}
                            onChange={(val) => changeStatementChargeName(idx, val)}
                            placeholder="Select statement charge"
                            displayKey="text"
                            valueKey="id"
                            popoutMode
                            menuPlacement="auto"
                            clearable={false}
                            className="statement-charge-select"
                            hidden={false}
                          />
                        )}
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>
                        {c.is_extra && (
                          <button
                            type="button"
                            onClick={() => zeroExtraAmount(idx)}
                            title="Set actual charge to 0"
                            style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 'bold', marginRight: 6 }}
                          >
                            -
                          </button>
                        )}
                        {c.currency === 'USD' ? '-' : formatCurrency(c.amount)}
                      </td>
                      {hasUsdCharges && (
                        <td style={{ ...cellStyle, textAlign: 'right' }}>{c.currency === 'USD' ? formatCurrency(c.amount) : '-'}</td>
                      )}
                      <td style={{ ...cellStyle, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={c.rounded}
                          onChange={() => toggleRoundOff(idx)}
                        />
                      </td>
                      <td
                        style={{ ...cellStyle, textAlign: 'right', ...(stmtDiff !== 0 ? { backgroundColor: '#fef08a' } : {}) }}
                        title={stmtDiff !== 0 ? `Difference: ${formatCurrency(stmtDiff)}` : undefined}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          value={editingStatementIdx === idx ? String(c.statement_amount || '') : formatCurrency(c.statement_amount)}
                          onFocus={() => setEditingStatementIdx(idx)}
                          onChange={(e) => changeStatementAmount(idx, e.target.value)}
                          onBlur={() => {
                            roundExtraStatementAmount(idx);
                            setEditingStatementIdx(null);
                          }}
                          style={{ width: '100%', textAlign: 'right', border: '1px solid #cbd5e1', borderRadius: 4, padding: 4 }}
                        />
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <span
                          draggable
                          onDragStart={(e) => {
                            dragSrc.current = idx;
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(idx));
                            const tr = e.currentTarget.closest('tr');
                            if (tr) e.dataTransfer.setDragImage(tr, 20, 10);
                          }}
                          onDragEnd={() => { dragSrc.current = null; }}
                          style={{ cursor: 'grab', marginRight: 8, fontSize: 15, color: '#64748b', userSelect: 'none' }}
                          title="Drag to reorder"
                        >
                          &#x2630;
                        </span>
                        {c.is_extra && (
                          <button
                            type="button"
                            onClick={() => removeExtraRow(c.id)}
                            style={{ color: 'red', background: 'none', border: 'none', cursor: 'pointer' }}
                            title="Remove row"
                          >
                            &times;
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button
                type="button"
                onClick={addExtraRow}
                style={{
                  background: '#e2e8f0',
                  border: '1px solid #cbd5e1',
                  borderRadius: 4,
                  padding: '6px 14px',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                }}
              >
                + Add extra charge
              </button>
              <div style={{ fontWeight: 'bold', fontSize: '1.05em' }}>
                Total Statement Amount: {formatCurrency(totalStatement)}
              </div>
            </div>

            {paidRows.length > 0 && (
              <div style={{ margin: '20px 0' }}>
                <h4 style={{ margin: '0 0 8px' }}>Client Paid</h4>
                <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%', position: 'relative', zIndex: 100 }}>
                  <thead>
                    <tr>
                      <th style={headerCellStyle}>Charge Name</th>
                      <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amount (TZS)</th>
                      <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amount (USD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientPaidGrouped.map(({ group, items }) => (
                      <React.Fragment key={group}>
                        <tr>
                          <td
                            onClick={() => toggleClientPaidGroup(group)}
                            style={{ ...cellStyle, fontWeight: 'bold', cursor: 'pointer' }}
                          >
                            <span style={{ color: '#dc2626', marginRight: 6 }}>
                              {expandedClientPaidGroups.has(group) ? '▼' : '▶'}
                            </span>
                            {group}
                          </td>
                          <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                            {formatCurrency(items.reduce((sum, cp) => sum + parseAmount(cp.tzs_amount), 0))}
                          </td>
                          <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                            {formatCurrency(items.reduce((sum, cp) => sum + parseAmount(cp.usd_amount), 0))}
                          </td>
                        </tr>
                        {expandedClientPaidGroups.has(group) && items.map((cp, i) => (
                          <tr key={i} style={{ background: '#f8fafc' }}>
                            <td style={{ ...cellStyle, paddingLeft: 40 }}>{toDisplay(cp.charge_name)}</td>
                            <td style={{ ...cellStyle, textAlign: 'right' }}>{cp.tzs_amount !== '' && cp.tzs_amount != null ? formatCurrency(cp.tzs_amount) : '-'}</td>
                            <td style={{ ...cellStyle, textAlign: 'right' }}>{cp.usd_amount !== '' && cp.usd_amount != null ? formatCurrency(cp.usd_amount) : '-'}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <button
                type="button"
                onClick={handlePreview}
                disabled={previewLoading || !charges.length}
                style={{
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  fontSize: '0.9rem',
                  cursor: (previewLoading || !charges.length) ? 'not-allowed' : 'pointer',
                  marginRight: 10,
                }}
              >
                {previewLoading ? 'Loading Preview…' : 'Preview'}
              </button>
              <button
                onClick={promptGenerate}
                disabled={generating}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  fontSize: '0.9rem',
                  cursor: generating ? 'not-allowed' : 'pointer',
                }}
              >
                {generating ? 'Saving…' : 'Save Debit Note'}
              </button>
            </div>

            {previewLoading && (
              <div style={{ color: '#94a3b8', marginTop: 12, textAlign: 'center' }}>Loading preview…</div>
            )}
            {previewUrl && (
              <iframe
                src={previewUrl}
                title="Debit Note Preview"
                style={{ width: '100%', height: 500, border: '1px solid #cbd5e1', marginTop: 12 }}
              />
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        okOnly={!!confirmDialog.okOnly}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

const ClientStatements = () => {
  const { hasPermission, permissions } = usePermissions();
  const isSuperuser = permissions?.is_superuser;
  const canView = hasPermission('CLIENT STATEMENTS', 'can_view');
  const canCreate = hasPermission('CLIENT STATEMENTS', 'can_create') || hasPermission('CLIENT STATEMENTS', 'can_edit');

  const [activeTab, setActiveTab] = useState('released');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return d.toISOString().split('T')[0];
  });
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [tabLoaded, setTabLoaded] = useState({});
  const [stage1Rows, setStage1Rows] = useState([]);
  const [stage2Rows, setStage2Rows] = useState([]);
  const [generatedRows, setGeneratedRows] = useState([]);
  const [generatedSubTab, setGeneratedSubTab] = useState('pending');
  const [ledgerSummaries, setLedgerSummaries] = useState({});
  // One entry per open Running Statement popout — unique key per window.
  const [runningStmtWindows, setRunningStmtWindows] = useState([]); // {key, customer, shortName}
  const [ledgerFilter, setLedgerFilter] = useState(null);
  const [rejectedRows, setRejectedRows] = useState([]);
  const [additionalRows, setAdditionalRows] = useState([]);
  const [payablePerms, setPayablePerms] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const loadCount = useRef(0);
  const beginLoad = useCallback(() => { loadCount.current += 1; setLoading(true); }, []);
  const endLoad = useCallback(() => {
    loadCount.current = Math.max(0, loadCount.current - 1);
    if (loadCount.current === 0) setLoading(false);
  }, []);
  const [generateWindows, setGenerateWindows] = useState([]);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [viewStatements, setViewStatements] = useState([]);
  const [custStatements, setCustStatements] = useState([]);
  const [adminStatements, setAdminStatements] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, row: null });
  const [viewPayableDetails, setViewPayableDetails] = useState([]);
  const [viewExpensesList, setViewExpensesList] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [showApprovalHistory, setShowApprovalHistory] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '' });
  const [emailModal, setEmailModal] = useState({ open: false, row: null });
  const [emailBankOptions, setEmailBankOptions] = useState([]);
  const [companyName, setCompanyName] = useState('');

  // Company name from Company Details — fetched once for the email body.
  useEffect(() => {
    fetch('/api/admin/company-details/', { headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const d = Array.isArray(data) ? data[0] : (data?.results?.[0] || data);
        setCompanyName(d?.company_name || d?.name || '');
      })
      .catch(() => {});
  }, []);

  // Load company bank accounts when the email modal opens for a client with
  // an outstanding balance — lets the user pick a bank (or CASH).
  useEffect(() => {
    if (!emailModal.open || !emailModal.statementContext?.balance_display) { setEmailBankOptions([]); return; }
    let cancelled = false;
    fetch('/api/admin/company-bank-accounts/', { headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data.results || []);
        setEmailBankOptions([
          ...list.map(b => ({ id: b.id, text: b.short_name || b.bank_name || b.name || `Bank ${b.id}`, bank: b })),
          { id: 'CASH', text: 'CASH', bank: { id: 'CASH' } },
        ]);
      })
      .catch(() => setEmailBankOptions([{ id: 'CASH', text: 'CASH', bank: { id: 'CASH' } }]));
    return () => { cancelled = true; };
  }, [emailModal.open]);
  const confirmActionRef = useRef(null);
  const activeTabLoaded = useRef(false);

  const canApprove = canCreate || hasPermission('CLIENT STATEMENTS', 'can_edit');
  const canEdit = hasPermission('CLIENT STATEMENTS', 'can_edit');
  const canDelete = hasPermission('CLIENT STATEMENTS', 'can_delete');

  const canApproveStage2 = useMemo(
    () => isSuperuser || payablePerms.some((p) => p.stage === 3 && p.can_approve_payables),
    [isSuperuser, payablePerms]
  );

  const canEditRejected = canApprove || canApproveStage2;

  const handleViewPayable = useCallback(async (it) => {
    if (!it.payable_type) return;
    if (it.payable_type === 'invoice') {
      try {
        const jobId = viewStatements.length
          ? viewStatements[viewStatements.length - 1].row.job
          : (generateWindows.length ? generateWindows[generateWindows.length - 1].job.id : null);
        if (!jobId) throw new Error('No job selected');
        const res = await fetch(`${API_INVOICE_PREVIEW}?job_id=${jobId}`, { headers: getAuthHeaders(false) });
        if (!res.ok) throw new Error('Failed to fetch invoice');
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        openFilePreview(blobUrl, 'Invoice.pdf');
      } catch (err) {
        alert(err.message);
      }
      return;
    }
    const apiUrl = it.payable_type === 'shipping_line' ? API_SHIPPING_LINE_PAYABLE : API_OTHER_PAYABLE;
    try {
      const res = await fetch(`${apiUrl}${it.payable_id}/`, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to fetch payable details');
      const data = await res.json();
      setViewPayableDetails((prev) => [...prev, { type: it.payable_type, data, windowId: `${it.payable_type}_${it.payable_id}_${Date.now()}` }]);
    } catch (err) {
      alert(err.message);
    }
  }, [viewStatements, generateWindows]);

  const fetchPendingJobs = useCallback(async () => {
    beginLoad();
    try {
      const params = new URLSearchParams({ start_date: startDate, customer_id: customerId || 'ALL' });
      if (appliedSearch) params.append('search', appliedSearch);
      const res = await fetch(`${API_BASE}/pending-jobs/?${params.toString()}`, {
        headers: getAuthHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to load pending jobs');
      const data = await res.json();
      setPendingRows(data || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load pending jobs');
      setPendingRows([]);
    } finally {
      setPendingLoaded(true);
      endLoad();
    }
  }, [startDate, customerId, appliedSearch, beginLoad, endLoad]);

  const fetchStatements = useCallback(async (status, setter, tabKey) => {
    try {
      const params = new URLSearchParams({ customer_id: customerId || 'ALL' });
      if (Array.isArray(status)) {
        status.forEach((s) => params.append('status', s));
      } else {
        params.append('status', status);
      }
      if (appliedSearch) params.append('search', appliedSearch);
      const res = await fetch(`${API_BASE}/?${params.toString()}`, {
        headers: getAuthHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to load statements');
      const data = await res.json();
      setter(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      toast.error(e.message || `Failed to load statements`);
      setter([]);
    } finally {
      if (tabKey) setTabLoaded((prev) => ({ ...prev, [tabKey]: true }));
    }
  }, [customerId, appliedSearch]);

  const fetchAdditionalJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ customer_id: customerId || 'ALL' });
      if (appliedSearch) params.append('search', appliedSearch);
      const res = await fetch(`${API_BASE}/additional-jobs/?${params.toString()}`, {
        headers: getAuthHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to load additional statements');
      const data = await res.json();
      setAdditionalRows(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load additional statements');
      setAdditionalRows([]);
    } finally {
      setTabLoaded((prev) => ({ ...prev, additional: true }));
    }
  }, [customerId, appliedSearch]);

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await fetch('/api/documentation/jobs/invoicing-customers/', { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load customers');
      const data = await res.json();
      setCustomers((Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), text: c.name, name: c.name })));
    } catch (e) {
      setCustomers([]);
    }
  }, []);

  const fetchPayablePerms = useCallback(async () => {
    try {
      const res = await fetch(API_PAYABLE_PERMS, { headers: getAuthHeaders(false) });
      if (res.ok) {
        const data = await res.json();
        setPayablePerms(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setPayablePerms([]);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchPayablePerms();
  }, [fetchPayablePerms]);

  const loadAllCounts = useCallback(async () => {
    await Promise.all([
      fetchPendingJobs(),
      fetchStatements('PENDING', setStage1Rows, 'stage1'),
      fetchStatements('APPROVED 1', setStage2Rows, 'stage2'),
      fetchStatements(['APPROVED 2', 'GENERATED'], setGeneratedRows, 'generated'),
      fetchStatements('REJECTED', setRejectedRows, 'rejected'),
      fetchAdditionalJobs(),
    ]);
  }, [fetchPendingJobs, fetchStatements, fetchAdditionalJobs]);

  const loadTab = useCallback(() => {
    if (activeTab === 'released') {
      fetchPendingJobs();
    } else if (activeTab === 'stage1') {
      fetchStatements('PENDING', setStage1Rows, 'stage1');
    } else if (activeTab === 'stage2') {
      fetchStatements('APPROVED 1', setStage2Rows, 'stage2');
    } else if (activeTab === 'generated') {
      fetchStatements(['APPROVED 2', 'GENERATED'], setGeneratedRows, 'generated');
    } else if (activeTab === 'rejected') {
      fetchStatements('REJECTED', setRejectedRows, 'rejected');
    } else if (activeTab === 'additional') {
      fetchAdditionalJobs();
    }
  }, [activeTab, fetchPendingJobs, fetchStatements, fetchAdditionalJobs]);

  useEffect(() => {
    loadAllCounts();
  }, [loadAllCounts]);

  useEffect(() => {
    if (activeTabLoaded.current) {
      loadTab();
    } else {
      activeTabLoaded.current = true;
    }
  }, [activeTab, loadTab]);

  // Reset the balance-card filter whenever the tab changes
  useEffect(() => { setLedgerFilter(null); }, [activeTab, generatedSubTab]);

  // Prefetch ledger summaries for every client so the Ledger cards can total them
  useEffect(() => {
    if (activeTab !== 'generated' || generatedSubTab !== 'ledger') return;
    const clients = [...new Set(generatedRows.map((r) => (r.customer_name || 'UNKNOWN CLIENT').toUpperCase()))]
      .filter((c) => !(c in ledgerSummaries));
    if (!clients.length) return;
    let cancelled = false;
    Promise.all(clients.map((c) =>
      fetch(`${API_BASE}/customer-ledger-summary/?customer=${encodeURIComponent(c)}`, { headers: getAuthHeaders(false) })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => [c, data])
        .catch(() => [c, null])
    )).then((entries) => {
      if (cancelled) return;
      setLedgerSummaries((prev) => {
        const next = { ...prev };
        entries.forEach(([c, d]) => { next[c] = d; });
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [activeTab, generatedSubTab, generatedRows, ledgerSummaries]);

  // DR = sum of positive balances, CR = sum of negative balances (per currency)
  const ledgerTotals = useMemo(() => {
    const clients = new Set(generatedRows.map((r) => (r.customer_name || 'UNKNOWN CLIENT').toUpperCase()));
    let drTzs = 0, crTzs = 0, drUsd = 0, crUsd = 0;
    clients.forEach((c) => {
      const s = ledgerSummaries[c];
      if (!s) return;
      const bt = Number(s.balance_tzs) || 0;
      const bu = Number(s.balance_usd) || 0;
      if (bt > 0) drTzs += bt; else if (bt < 0) crTzs += -bt;
      if (bu > 0) drUsd += bu; else if (bu < 0) crUsd += -bu;
    });
    return { drTzs, crTzs, drUsd, crUsd, totalTzs: drTzs - crTzs, totalUsd: drUsd - crUsd };
  }, [generatedRows, ledgerSummaries]);

  // Multi-window helpers — each popout gets its own windowId, like Payables
  const openStatementWindow = (row) => {
    const existing = viewStatements.find((e) => e.row.id === row.id);
    if (existing) {
      // Statement already open — focus its window instead of opening a duplicate
      window.open('', `statement_view_${row.id}_${existing.windowId}`);
      return;
    }
    setViewStatements((prev) => [...prev, { row, windowId: `${row.id}_${Date.now()}` }]);
  };
  const generateWindowName = (e) => `statement_window_${e.job.id}_${e.windowId}`;
  const openGenerateWindow = (job, editRow = null, excludeStatementId = null) => {
    const existing = generateWindows.find((e) =>
      editRow
        ? (e.editRow && e.editRow.id === editRow.id)
        : (!e.editRow && e.job.id === job.id && (e.excludeStatementId || null) === (excludeStatementId || null))
    );
    if (existing) {
      window.open('', generateWindowName(existing));
      return;
    }
    setGenerateWindows((prev) => [
      ...prev,
      { job, editRow, excludeStatementId, windowId: `${job.id}_${Date.now()}` },
    ]);
  };
  const closeGenerateWindow = (windowId) => {
    const entry = generateWindows.find((e) => e.windowId === windowId);
    setGenerateWindows((prev) => prev.filter((e) => e.windowId !== windowId));
    if (entry && entry.editRow) {
      fetchStatements('REJECTED', setRejectedRows);
    }
  };
  const closeStatementWindow = (windowId) => {
    setViewStatements((prev) => prev.filter((e) => e.windowId !== windowId));
    setRejectionReasons((prev) => {
      if (!(windowId in prev)) return prev;
      const next = { ...prev };
      delete next[windowId];
      return next;
    });
    setShowApprovalHistory((prev) => {
      if (!(windowId in prev)) return prev;
      const next = { ...prev };
      delete next[windowId];
      return next;
    });
  };
  const closeStatementsForRow = (rowId) =>
    setViewStatements((prev) => prev.filter((e) => e.row.id !== rowId));
  const openExpensesWindow = (job) =>
    setViewExpensesList((prev) => [...prev, { id: job.id, idf_no: job.idf_no || '', windowId: `${job.id}_${Date.now()}` }]);
  const closeExpensesWindow = (windowId) =>
    setViewExpensesList((prev) => prev.filter((e) => e.windowId !== windowId));
  const closePayableDetailWindow = (windowId) =>
    setViewPayableDetails((prev) => prev.filter((e) => e.windowId !== windowId));

  const handleCustomerChange = (val) => { setCustomerId(val || ''); setSearch(''); setAppliedSearch(''); };
  const handleApplySearch = () => { setAppliedSearch(search); };
  const handleClear = () => { setSearch(''); setAppliedSearch(''); };
  const handleStartDateChange = (e) => {
    const v = e.target.value;
    setStartDate(v);
    localStorage.setItem('kk_statement_start_date', v);
  };

  const handleApprove = async (row) => {
    setActionLoading((prev) => ({ ...prev, [row.id]: true }));
    try {
      const res = await fetch(`${API_BASE}/${row.id}/approve/`, {
        method: 'POST',
        headers: getAuthHeaders(false),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to approve');
      toast.success(`Approved to ${data.status || 'next stage'}`);
      loadAllCounts();
      closeStatementsForRow(row.id);
    } catch (e) {
      toast.error(e.message || 'Failed to approve');
    } finally {
      setActionLoading((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const handleReject = async (row, reason) => {
    const trimmed = (reason || '').trim();
    if (!trimmed) {
      toast.error('Rejection reason is required');
      return;
    }
    setActionLoading((prev) => ({ ...prev, [row.id]: true }));
    try {
      const res = await fetch(`${API_BASE}/${row.id}/reject/`, {
        method: 'POST',
        headers: getAuthHeaders(false),
        body: JSON.stringify({ reason: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to reject');
      toast.success('Statement rejected');
      loadAllCounts();
      closeStatementsForRow(row.id);
    } catch (e) {
      toast.error(e.message || 'Failed to reject');
    } finally {
      setActionLoading((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const openGenerate = (job, excludeStatementId = null) => {
    openGenerateWindow(job, null, excludeStatementId);
  };

  const openEdit = (row) => {
    openGenerateWindow({ ...row, id: row.job }, row, null);
  };

  const openAdditionalStatement = (row) => {
    openGenerate({
      id: row.job,
      idf_no: row.idf_no,
      customer_name: row.customer_name,
      customer_short_name: row.customer_short_name,
      supplier_name: row.supplier_name,
      bl: row.bl,
      shipment_type: row.shipment_type,
      cargo_type: row.cargo_type,
      transporter: row.transporter,
      release_date: row.release_date,
      client_ref_no: row.client_ref_no,
    }, row.id);
  };

  const renderStatementApprovalHistory = (row, windowId) => {
    const createdEntry = row.created_by_name && row.created_at ? [{ key: 'created', label: 'CREATED BY:', by: row.created_by_name, at: row.created_at, isCreated: true }] : [];
    const approvalEntries = [
      { key: 'stage1', label: 'STAGE 1 APPROVED:', by: row.approved_stage_1_by_name, at: row.approved_stage_1_at },
      { key: 'stage2', label: 'STAGE 2 APPROVED:', by: row.approved_stage_2_by_name, at: row.approved_stage_2_at },
    ]
      .map((e) => ({ ...e, timestamp: Date.parse(e.at) }))
      .filter((e) => e.by && e.at && !Number.isNaN(e.timestamp));
    const rejectionAt = row.rejected_at || row.updated_at || row.created_at;
    const rejectionEntry = (row.rejection_reason || row.status === 'REJECTED') ? [{ key: 'rejection', label: 'REJECTED:', by: row.rejected_by_name || '-', at: rejectionAt, reason: row.rejection_reason || '', isRejection: true, timestamp: Date.parse(rejectionAt) || 0 }] : [];
    const allEntries = [
      ...createdEntry,
      ...[...approvalEntries, ...rejectionEntry].sort((a, b) => a.timestamp - b.timestamp)
    ].filter((e) => !Number.isNaN(e.timestamp));
    return (
      <div style={{ marginBottom: 12, padding: '6px', background: '#f8f9fa', borderRadius: 5, border: '1px solid #dee2e6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderBottom: '1px solid #dee2e6', marginBottom: 5, paddingBottom: 3 }}>
          <button type="button" className="icon-btn" onClick={() => setShowApprovalHistory((p) => ({ ...p, [windowId]: !p[windowId] }))} style={{ color: '#555' }}>
            <i className={`fa ${showApprovalHistory[windowId] ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
          </button>
          <div style={{ fontWeight: 'bold' }}>APPROVAL HISTORY</div>
        </div>
        {showApprovalHistory[windowId] && (
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #dee2e6', marginTop: 5 }}>
            <thead>
              <tr style={{ background: '#e9ecef' }}>
                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>EVENT</th>
                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>USER</th>
                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'center', fontWeight: 'bold' }}>DATE</th>
                <th style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', fontWeight: 'bold' }}>REJECTION REASON</th>
              </tr>
            </thead>
            <tbody>
              {allEntries.map((entry) => (
                <tr
                  key={entry.key}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', fontWeight: 'bold', color: entry.isCreated ? '#444' : (entry.isRejection ? '#c0392b' : '#666'), whiteSpace: 'nowrap' }}>{entry.label}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{entry.by}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'center', color: '#888', whiteSpace: 'nowrap' }}>{formatDateTimeDisplay(entry.at)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px 8px', textAlign: 'left', color: 'red', whiteSpace: 'pre-wrap' }}>{(entry.reason || '').toUpperCase()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const promptApprove = (row, windowId = null) => {
    confirmActionRef.current = () => handleApprove(row);
    setConfirmDialog({
      open: true,
      title: 'Confirm Approve',
      message: `Are you sure you want to approve statement ${row.idf_no || row.id}?`,
      windowId,
    });
  };

  const promptReject = (row, windowId = null) => {
    confirmActionRef.current = () => handleReject(row, rejectionReasons[windowId] || '');
    setConfirmDialog({
      open: true,
      title: 'Confirm Reject',
      message: `Are you sure you want to reject statement ${row.idf_no || row.id}?`,
      windowId,
    });
  };

  const handleConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '' });
    if (confirmActionRef.current) {
      confirmActionRef.current();
      confirmActionRef.current = null;
    }
  };

  const renderReleasedJobsTable = () => (
    <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%', position: 'relative', zIndex: 100 }}>
      <thead>
        <tr>
          <th style={headerCellStyle}>IDF</th>
          <th style={headerCellStyle}>BL</th>
          <th style={headerCellStyle}>Customer</th>
          <th style={headerCellStyle}>Release Date</th>
          <th style={{ ...headerCellStyle, textAlign: 'center' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {!pendingLoaded ? (
          <tr>
            <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>
              Loading… please wait
            </td>
          </tr>
        ) : pendingRows.filter((r) => r.invoice_no || r.is_active === false).length === 0 && (
          <tr>
            <td colSpan={5} style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>
              No released jobs found.
            </td>
          </tr>
        )}
        {pendingRows.filter((r) => r.invoice_no || r.is_active === false).map((row, idx) => (
          <tr
            key={row.id}
            className={[
              row.is_active === false ? '' : `dn-row ${idx % 2 === 0 ? 'dn-row-alt' : ''}`,
              row.is_rejected ? 'rejected-flash' : '',
            ].join(' ').trim() || undefined}
            style={row.is_active === false ? { backgroundColor: '#f5c6cb' } : undefined}
            title={row.is_active === false ? 'JOB CLOSED, INACTIVED' : undefined}
          >
            <td style={{ ...cellStyle, padding: '2px 6px' }}>{toDisplay(row.idf_no)}</td>
            <td style={{ ...cellStyle, padding: '2px 6px' }}>{toDisplay(row.bl)}</td>
            <td style={{ ...cellStyle, padding: '2px 6px' }}><strong>{toDisplay(row.customer_name)}</strong></td>
            <td style={{ ...cellStyle, padding: '2px 6px' }}>{formatDate(row.release_date)}</td>
            <td style={{ ...cellStyle, padding: '2px 6px', textAlign: 'center' }}>
              {canCreate && (
                <button
                  className="add-btn"
                  title="Generate Debit Note"
                  onClick={() => openGenerate(row)}
                  style={{ background: 'transparent', color: '#007bff', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
                >
                  +
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderAdditionalTable = () => (
    <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%', position: 'relative', zIndex: 100 }}>
      <thead>
        <tr>
          <th style={headerCellStyle}>IDF</th>
          <th style={headerCellStyle}>BL</th>
          <th style={headerCellStyle}>Customer</th>
          <th style={headerCellStyle}>Statement No</th>
          <th style={headerCellStyle}>Generated On</th>
          <th style={{ ...headerCellStyle, textAlign: 'center' }}>New Charges</th>
          <th style={{ ...headerCellStyle, textAlign: 'center' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {additionalRows.length === 0 && (
          <tr>
            <td colSpan={7} style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>
              {tabLoaded.additional ? 'No additional charges found.' : 'Loading… please wait'}
            </td>
          </tr>
        )}
        {additionalRows.map((row) => (
          <tr key={row.id}>
            <td style={cellStyle}>{toDisplay(row.idf_no)}</td>
            <td style={cellStyle}>{toDisplay(row.bl)}</td>
            <td style={cellStyle}><strong>{toDisplay(row.customer_name)}</strong></td>
            <td style={cellStyle}>{toDisplay(row.statement_no)}</td>
            <td style={cellStyle}>{formatDateTimeDisplay(row.generated_at)}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>{row.new_charges_count}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>
              {canCreate && (
                <button
                  className="add-btn"
                  title="Generate Additional Debit Note"
                  onClick={() => openAdditionalStatement(row)}
                  style={{ background: 'transparent', color: '#007bff', width: 28, height: 28, padding: 0, fontSize: 18, border: 'none', cursor: 'pointer' }}
                >
                  +
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderStatementTable = (rows, empty, onEdit, showApprovedBy = false, loaded = true) => (
    <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%', position: 'relative', zIndex: 100 }}>
      <thead>
        <tr>
          <th style={headerCellStyle}>IDF</th>
          <th style={headerCellStyle}>Customer</th>
          <th style={headerCellStyle}>Statement Date</th>
          {showApprovedBy && <th style={headerCellStyle}>Approved By</th>}
          <th style={{ ...headerCellStyle, textAlign: 'right' }}>Total (TZS)</th>
          <th style={headerCellStyle}>Created By</th>
          <th style={headerCellStyle}>Rejection Reason</th>
          <th style={{ ...headerCellStyle, textAlign: 'center' }}>Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={showApprovedBy ? 8 : 7} style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>
              {loaded ? empty : 'Loading… please wait'}
            </td>
          </tr>
        )}
        {rows.map((row) => (
          <tr key={row.id} style={row.rejection_reason ? { backgroundColor: '#fee2e2' } : undefined}>
            <td style={cellStyle}>{toDisplay(row.idf_no)}</td>
            <td style={cellStyle}><strong>{toDisplay(row.customer_name)}</strong></td>
            <td style={cellStyle}>{formatDate(row.statement_date)}</td>
            {showApprovedBy && <td style={cellStyle}>{toDisplay(row.approved_stage_1_by_name)}</td>}
            <td style={{ ...cellStyle, textAlign: 'right' }}>{formatCurrency(row.total_amount)}</td>
            <td style={cellStyle}>{toDisplay(row.generated_by_name || row.created_by_name)}</td>
            <td style={cellStyle}>{toDisplay(row.rejection_reason)}</td>
            <td style={{ ...cellStyle, textAlign: 'center' }}>
              <button
                className="icon-btn"
                title="View Statement"
                onClick={() => openStatementWindow(row)}
                style={{ color: 'blue', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
              >
                <i className="fa fa-eye"></i>
              </button>
              {onEdit && (
                <button
                  className="icon-btn"
                  title="Edit Statement"
                  onClick={() => onEdit(row)}
                  style={{ color: '#16a34a', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
                >
                  <i className="fa fa-pencil"></i>
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderStage1Table = () =>
    renderStatementTable(stage1Rows, 'No records.', null, false, tabLoaded.stage1);

  const renderStage2Table = () =>
    renderStatementTable(stage2Rows, 'No records.', null, true, tabLoaded.stage2);

  const renderRejectionsTable = () =>
    renderStatementTable(rejectedRows, 'No rejected statements.', canEditRejected ? openEdit : null, false, tabLoaded.rejected);

  const toggleClient = (client) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(client)) next.delete(client);
      else next.add(client);
      return next;
    });
  };

  const openStatementPreview = async (row, type) => {
    const url = type === 'pdf'
      ? `${API_BASE}/${row.id}/pdf-preview/`
      : `${API_BASE}/${row.id}/download/`;
    const filename = type === 'pdf'
      ? `${row.statement_no || ''}) ${row.kk_file_no || ''} IDF ${row.idf_no || ''}.pdf`
      : `${row.statement_no || ''}) ${row.kk_file_no || ''} IDF ${row.idf_no || ''}.xlsx`;
    try {
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Failed to load preview');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      openFilePreview(objectUrl, filename);
    } catch (e) {
      toast.error(e.message || 'Failed to load preview');
    }
  };

  const handleEmailStatement = async (row) => {
    const toISO = (d) => d.toISOString().split('T')[0];
    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    let stmtFrom = toISO(monthAgo);
    const stmtTo = toISO(today);

    let emailGroups = [];
    let clientEmail = '';
    let jobDocuments = [];
    let jobInvoiceNo = '';
    let ledgerBalanceDisplay = '';
    let staffSignature = null;
    try {
      const jobRes = await fetch(`/api/documentation/jobs/${row.job}/`, { headers: getAuthHeaders(false) });
      const jobData = jobRes.ok ? await jobRes.json().catch(() => null) : null;
      const customerId = jobData ? jobData.customer : null;
      jobInvoiceNo = (jobData && jobData.invoice_no) || '';
      const [custRes, docsRes, ledgerRes, sigRes] = await Promise.all([
        customerId
          ? fetch(`/api/masters/customers/${customerId}/`, { headers: getAuthHeaders(false) })
          : Promise.resolve(null),
        fetch(`/api/documentation/jobs/${row.job}/documents/`, { headers: getAuthHeaders(false) }),
        fetch(`${API_BASE}/customer-ledger-summary/?customer=${encodeURIComponent(row.customer_name || '')}`, { headers: getAuthHeaders(false) }),
        fetch('/api/admin/staff/me/', { headers: getAuthHeaders(false) }),
      ]);
      if (sigRes && sigRes.ok) {
        staffSignature = await sigRes.json().catch(() => null);
      }
      if (ledgerRes && ledgerRes.ok) {
        const summary = await ledgerRes.json().catch(() => null);
        if (summary) {
          const parts = [];
          if ((summary.balance_tzs || 0) > 0) parts.push(`TZS ${formatCurrency(summary.balance_tzs)}`);
          if ((summary.balance_usd || 0) > 0) parts.push(`USD ${formatCurrency(summary.balance_usd)}`);
          ledgerBalanceDisplay = parts.join(' / ');
        }
      }
      if (custRes && custRes.ok) {
        const custData = await custRes.json().catch(() => null);
        emailGroups = (custData && custData.email_groups) || [];
        clientEmail = emailGroups.map((g) => g.emails).filter(Boolean).join('; ');
        // Never start before the balance brought forward date
        const bbfDate = custData && custData.balance_brought_forward_date;
        if (bbfDate && bbfDate > stmtFrom) {
          stmtFrom = bbfDate;
        }
      }
      if (docsRes && docsRes.ok) {
        const docsData = await docsRes.json().catch(() => []);
        jobDocuments = Array.isArray(docsData) ? docsData : [];
      }
    } catch (e) {
      console.error('Failed to load email context:', e);
    }

    const fileName = getStatementFileName(row);
    const debitNoteName = fileName !== '-'
      ? fileName.replace(/\.xlsx$/i, '.pdf')
      : `Debit_Note_${row.idf_no || row.id}.pdf`;
    const stmtPdfUrl = `${API_BASE}/customer-statement-pdf/?customer=${encodeURIComponent(row.customer_name || '')}&from=${stmtFrom}&to=${stmtTo}`;
    const generatedDocs = [
      {
        id: `debit-note-${row.id}`,
        url: `${API_BASE}/${row.id}/pdf-preview/`,
        attach_url: `${API_BASE}/${row.id}/download-pdf/`,
        name: debitNoteName,
        document_type: 'Debit Note',
      },
      {
        id: `customer-statement-${row.id}`,
        url: stmtPdfUrl,
        attach_url: stmtPdfUrl,
        name: `Customer_Statement_${stmtFrom}_to_${stmtTo}.pdf`,
        document_type: 'Statement',
      },
    ];

    setEmailModal({
      open: true,
      row,
      jobAttachments: [...jobDocuments, ...generatedDocs],
      emailGroups,
      clientEmail,
      officeEmail: (staffSignature && staffSignature.office_email) || '',
      statementContext: {
        statement_id: row.id,
        statement_no: row.statement_no || '',
        supplier_name: row.supplier_name || '',
        invoice_no: jobInvoiceNo,
        bl_no: row.bl || '',
        balance_display: ledgerBalanceDisplay,
        signature_image: (staffSignature && staffSignature.signature_image) || '',
        email_signature: (staffSignature && staffSignature.email_signature) || '',
      },
    });
  };

  const stmtRangePresets = [
    { id: 'all', label: 'All' },
    { id: 'minus1d', label: '-1d' },
    { id: 'minus3d', label: '-3d' },
    { id: 'minus7d', label: '-7d' },
    { id: 'minus1m', label: '-1M' },
    { id: 'minus3m', label: '-3M' },
    { id: 'range', label: 'Date Range' },
  ];
  const stmtPresetRange = (id) => {
    const today = new Date();
    const iso = (d) => d.toISOString().split('T')[0];
    const back = (days) => { const d = new Date(today); d.setDate(d.getDate() - days); return iso(d); };
    const backM = (months) => { const d = new Date(today); d.setMonth(d.getMonth() - months); return iso(d); };
    switch (id) {
      case 'minus1d': return { from: back(1), to: iso(today) };
      case 'minus3d': return { from: back(3), to: iso(today) };
      case 'minus7d': return { from: back(7), to: iso(today) };
      case 'minus1m': return { from: backM(1), to: iso(today) };
      case 'minus3m': return { from: backM(3), to: iso(today) };
      default: return { from: '', to: '' };
    }
  };
  const applyCustStmtPreset = (windowId, name, presetId) => {
    if (presetId === 'range') {
      updateCustStatement(windowId, { preset: 'range' });
      return;
    }
    const range = stmtPresetRange(presetId);
    updateCustStatement(windowId, { preset: presetId, from: range.from, to: range.to });
    generateCustStatement(windowId, name, range.from, range.to);
  };

  const custStmtParams = (name, from, to) => {
    const p = new URLSearchParams();
    p.set('customer', name || '');
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    return p.toString();
  };

  const updateCustStatement = (windowId, patch) =>
    setCustStatements((prev) => prev.map((e) => (e.windowId === windowId ? { ...e, ...patch } : e)));

  const generateCustStatement = async (windowId, name, from = '', to = '') => {
    updateCustStatement(windowId, { loading: true });
    try {
      const res = await fetch(`${API_BASE}/customer-statement-pdf/?${custStmtParams(name, from, to)}`, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to generate statement');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setCustStatements((prev) => prev.map((e) => {
        if (e.windowId !== windowId) return e;
        if (e.url) URL.revokeObjectURL(e.url);
        return { ...e, url, loading: false };
      }));
    } catch (e) {
      toast.error(e.message || 'Could not generate statement.', { containerId: `cust-stmt-toast-${windowId}` });
      updateCustStatement(windowId, { loading: false });
    }
  };

  const downloadCustStatement = async (windowId, kind) => {
    const entry = custStatements.find((e) => e.windowId === windowId);
    if (!entry) return;
    try {
      const url = `${API_BASE}/customer-statement-${kind === 'pdf' ? 'pdf' : 'excel'}/?${custStmtParams(entry.name, entry.from, entry.to)}`;
      const res = await fetch(url, { headers: getAuthHeaders(false) });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      const blobUrl = URL.createObjectURL(blob);
      a.href = blobUrl;
      a.download = `Customer_Statement_${(entry.name || 'customer').replace(/[^\w]+/g, '_')}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(e.message || 'Download failed', { containerId: `cust-stmt-toast-${windowId}` });
    }
  };

  const openCustomerStatement = (name) => {
    const windowId = `${name.replace(/\W+/g, '_')}_${Date.now()}`;
    const range = stmtPresetRange('minus1m');
    const entry = { name, windowId, preset: 'minus1m', from: range.from, to: range.to, url: null, loading: false };
    setCustStatements((prev) => [...prev, entry]);
    generateCustStatement(windowId, name, entry.from, entry.to);
  };

  const closeCustomerStatement = (windowId) => {
    setCustStatements((prev) => {
      const entry = prev.find((e) => e.windowId === windowId);
      if (entry?.url) URL.revokeObjectURL(entry.url);
      return prev.filter((e) => e.windowId !== windowId);
    });
  };

  const openAdminStatement = async (client) => {
    const windowId = `${client.replace(/\W+/g, '_')}_${Date.now()}`;
    const range = stmtPresetRange('minus1m');
    setAdminStatements((prev) => [...prev, { name: client, windowId, preset: 'minus1m', from: range.from, to: range.to, loading: true, url: null }]);
    generateAdminStatement(windowId, client, range.from, range.to);
  };

  const generateAdminStatement = async (windowId, client, from = '', to = '') => {
    setAdminStatements((prev) => prev.map((e) => (e.windowId === windowId ? { ...e, loading: true, from, to } : e)));
    try {
      const res = await fetch(`${API_BASE}/customer-statement-admin-pdf/?${custStmtParams(client, from, to)}`, { headers: getAuthHeaders(false) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to load admin statement');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAdminStatements((prev) => prev.map((e) => {
        if (e.windowId !== windowId) return e;
        if (e.url) URL.revokeObjectURL(e.url);
        return { ...e, loading: false, url };
      }));
    } catch (e) {
      toast.error(e.message || 'Failed to load admin statement');
      setAdminStatements((prev) => prev.filter((x) => x.windowId !== windowId));
    }
  };

  const applyAdminStmtPreset = (windowId, client, presetId) => {
    if (presetId === 'range') {
      setAdminStatements((prev) => prev.map((e) => (e.windowId === windowId ? { ...e, preset: 'range' } : e)));
      return;
    }
    const range = stmtPresetRange(presetId);
    setAdminStatements((prev) => prev.map((e) => (e.windowId === windowId ? { ...e, preset: presetId } : e)));
    generateAdminStatement(windowId, client, range.from, range.to);
  };

  const closeAdminStatement = (windowId) => {
    setAdminStatements((prev) => {
      const entry = prev.find((e) => e.windowId === windowId);
      if (entry?.url) URL.revokeObjectURL(entry.url);
      return prev.filter((e) => e.windowId !== windowId);
    });
  };

  const promptDeleteStatement = (row) => {
    setDeleteConfirm({ open: true, row });
  };

  const handleDeleteStatement = async () => {
    const row = deleteConfirm.row;
    if (!row) return;
    setDeleteConfirm({ open: false, row: null });
    setActionLoading((prev) => ({ ...prev, [row.id]: true }));
    try {
      const res = await fetch(`${API_BASE}/${row.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(false),
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to delete');
      }
      toast.success(`Debit note ${row.statement_no || row.id} deleted`);
      loadAllCounts();
    } catch (e) {
      toast.error(e.message || 'Failed to delete debit note');
    } finally {
      setActionLoading((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  if (!canView) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Permission Denied</h2>
        <p>You do not have permission to view Client Statements.</p>
      </div>
    );
  }

  const renderGeneratedTable = (rows = generatedRows) => {
    if (rows.length === 0) {
      const emptyMsg = generatedSubTab === 'pending' ? 'No pending statements.' : 'No generated statements.';
      return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 20 }}>{tabLoaded.generated ? emptyMsg : 'Loading… please wait'}</div>;
    }

    const grouped = new Map();
    [...rows].sort((a, b) => {
      const clientA = (a.customer_name || '').toUpperCase();
      const clientB = (b.customer_name || '').toUpperCase();
      if (clientA !== clientB) return clientA.localeCompare(clientB);
      const dateA = new Date(a.generated_at || a.created_at || 0);
      const dateB = new Date(b.generated_at || b.created_at || 0);
      return dateB - dateA;
    }).forEach((row) => {
      const client = (row.customer_name || 'UNKNOWN CLIENT').toUpperCase();
      if (!grouped.has(client)) grouped.set(client, []);
      grouped.get(client).push(row);
    });

    const clients = Array.from(grouped.keys()).sort();

    // Ledger balance-card filter: only clients whose balance matches the selected sign/currency
    const visibleClients = (generatedSubTab === 'ledger' && ledgerFilter)
      ? clients.filter((c) => {
          const s = ledgerSummaries[c];
          if (!s) return false;
          const bal = ledgerFilter.endsWith('usd') ? (Number(s.balance_usd) || 0) : (Number(s.balance_tzs) || 0);
          return ledgerFilter.startsWith('dr') ? bal > 0 : bal < 0;
        })
      : clients;

    // Expanded debit-note rows styled like the EIR report's expanded sections
    const dnHeaderStyle = { border: '1px solid #b9c9d8', padding: 6, background: '#fff4cc', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' };
    const dnCellStyle = { border: '1px solid #d4dce3', padding: 6, whiteSpace: 'nowrap' };

    return (
      <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>S/N</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Client</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}># DN</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>DR TZS</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>CR TZS</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Balance TZS</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>DR USD</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>CR USD</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Balance USD</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>View Stmt</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}></th>
          </tr>
        </thead>
        <tbody>
          {visibleClients.length === 0 && (
            <tr><td colSpan={11} style={{ ...cellStyle, textAlign: 'center', color: '#94a3b8' }}>No records match the selected balance filter.</td></tr>
          )}
          {visibleClients.map((client, idx) => {
            const rows = grouped.get(client);
            const expanded = expandedClients.has(client);
            return (
              <React.Fragment key={client}>
                <tr
                  onClick={() => toggleClient(client)}
                  style={{ backgroundColor: idx % 2 === 0 ? '#f3e5f5' : '#fff', cursor: 'pointer' }}
                >
                  <td style={{ ...cellStyle, textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ ...cellStyle, fontWeight: 700 }}>{toDisplay(rows[0]?.customer_short_name || client)}</td>
                  <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 600 }}>{rows.length}</td>
                  <ClientLedgerSummaryCells customerName={client} summary={ledgerSummaries[client]} />
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <button
                      className="icon-btn"
                      title="View Statement"
                      onClick={(e) => { e.stopPropagation(); openCustomerStatement(client); }}
                      style={{ color: '#2563eb', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 15, padding: '0 4px' }}
                    >
                      <i className="fa fa-eye"></i>
                    </button>
                    <button
                      className="icon-btn"
                      title="Running Statement"
                      onClick={(e) => {
                        e.stopPropagation();
                        const key = `rs_${client.replace(/\s+/g, '_')}_${Date.now()}`;
                        setRunningStmtWindows((prev) => [...prev, { key, customer: client, shortName: rows[0]?.customer_short_name || client }]);
                      }}
                      style={{ color: '#16a34a', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 15, padding: '0 4px' }}
                    >
                      <i className="fa fa-running"></i>
                    </button>
                    {canDelete && (
                      <button
                        className="icon-btn"
                        title="Admin Statement"
                        onClick={(e) => { e.stopPropagation(); openAdminStatement(client); }}
                        style={{ color: '#dc2626', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 15, padding: '0 4px' }}
                      >
                        <i className="fa fa-eye"></i>
                      </button>
                    )}
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center', fontSize: 12, color: '#64748b' }}>{expanded ? '▲' : '▼'}</td>
                </tr>
                {expanded && (
                  <>
                  <tr>
                    <td colSpan={11} style={{ padding: '8px 0 8px 16px', border: 'none' }}>
                      <div style={{ borderLeft: '4px solid #f0ad4e', paddingLeft: 12 }}>
                      <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={dnHeaderStyle}>Statement No</th>
                            <th style={dnHeaderStyle}>IDF</th>
                            <th style={dnHeaderStyle}>File No.</th>
                            <th style={dnHeaderStyle}>Statement Date</th>
                            <th style={dnHeaderStyle}>Date Generated</th>
                            <th style={dnHeaderStyle}>Approved By</th>
                            <th style={{ ...dnHeaderStyle, textAlign: 'right' }}>Total (TZS)</th>
                            <th style={{ ...dnHeaderStyle, textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, dnIdx) => (
                        <tr key={row.id} className={`dn-row ${dnIdx % 2 === 0 ? 'dn-row-alt' : ''}`}>
                          <td style={dnCellStyle}>{toDisplay(row.statement_no)}</td>
                          <td style={dnCellStyle}>{toDisplay(row.idf_no)}</td>
                          <td style={dnCellStyle}>{toDisplay(row.kk_file_no)}</td>
                          <td style={dnCellStyle}>{formatDate(row.statement_date)}</td>
                          <td style={dnCellStyle}>{formatDate(row.generated_at)}</td>
                          <td style={dnCellStyle}>{toDisplay(row.approved_stage_2_by_name)}</td>
                          <td style={{ ...dnCellStyle, textAlign: 'right' }}>{formatCurrency(row.total_amount)}</td>
                          <td style={{ ...dnCellStyle, textAlign: 'center' }}>
                            <button
                              className="icon-btn"
                              title="View"
                              onClick={() => openStatementWindow(row)}
                              style={{ color: 'blue', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                            {['APPROVED 2', 'GENERATED'].includes(row.status) && (
                              <>
                                <button
                                  className="icon-btn"
                                  title="Preview PDF"
                                  onClick={() => openStatementPreview(row, 'pdf')}
                                  style={{ color: '#dc2626', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
                                >
                                  <i className="fa fa-file-pdf"></i>
                                </button>
                                <button
                                  className="icon-btn"
                                  title="Preview Excel"
                                  onClick={() => openStatementPreview(row, 'excel')}
                                  style={{ color: '#16a34a', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
                                >
                                  <i className="fa fa-file-excel"></i>
                                </button>
                              </>
                            )}
                            {(canEdit || canDelete) && (
                              <button
                                className="icon-btn"
                                title={row.email_sent_at ? `Email Sent on: ${formatDateTimeDisplay(row.email_sent_at)}` : "Email Statement"}
                                onClick={() => handleEmailStatement(row)}
                                style={{ color: row.email_sent_at ? '#16a34a' : '#2563eb', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
                              >
                                <i className="fa fa-envelope"></i>
                              </button>
                            )}
                            {canDelete && (
                              <>
                                <button
                                  className="icon-btn"
                                  title="Delete Debit Note"
                                  onClick={() => promptDeleteStatement(row)}
                                  disabled={!!actionLoading[row.id]}
                                  style={{ color: '#dc2626', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, padding: '0 6px' }}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </>
                            )}
                            </td>
                          </tr>
                        ))}
                        </tbody>
                      </table>
                      </div>
                    </td>
                  </tr>
                  <tr><td colSpan={11} style={{ height: 8, border: 'none', backgroundColor: '#cecdcd' }} /></tr>
                  </>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="client-reports-page" style={{ padding: 16 }}>
      <style>
        {`@keyframes rejected-flash {
          0%, 100% { background-color: #fee2e2; }
          50% { background-color: #fecaca; }
        }
        .rejected-flash {
          animation: rejected-flash 1.2s infinite;
        }
        @keyframes rejected-tab-pulse {
          0%, 100% { background-color: #fee2e2; color: #dc2626; }
          50% { background-color: #fecaca; color: #b91c1c; }
        }
        .rejected-tab-alert {
          color: #dc2626 !important;
          background-color: #fee2e2;
          animation: rejected-tab-pulse 1.2s infinite;
          font-weight: 700;
        }
        .custom-tab.cs-rejected-tab { background-color: #fecaca; color: #b91c1c; }
        .custom-tab.cs-rejected-tab:not(.active):hover { background-color: #fca5a5; }
        .custom-tab.cs-rejected-tab.active { background-color: #dc2626; color: #fff; }
        .custom-tab.cs-additional-tab { background-color: #fdba74; color: #9a3412; }
        .custom-tab.cs-additional-tab:not(.active):hover { background-color: #fb923c; }
        .custom-tab.cs-additional-tab.active { background-color: #ea580c; color: #fff; }
        .custom-tab.cs-generated-tab { background-color: #86efac; color: #166534; }
        .custom-tab.cs-generated-tab:not(.active):hover { background-color: #4ade80; }
        .custom-tab.cs-generated-tab.active { background-color: #16a34a; color: #fff; }
        @keyframes additional-tab-pulse {
          0%, 100% { background-color: #fdba74; color: #9a3412; }
          50% { background-color: #fb923c; color: #fff; }
        }
        .additional-tab-alert {
          animation: additional-tab-pulse 1.2s infinite;
          font-weight: 700;
        }
        @keyframes balance-dr-flash {
          0%, 100% { color: #c62828; opacity: 1; }
          50% { color: #ef5350; opacity: 0.35; }
        }
        .balance-dr-flash {
          animation: balance-dr-flash 1s ease-in-out infinite;
        }
        .cp-table tbody tr.dn-row-alt td {
          background-color: #e3f2fd;
        }
        .cp-table tbody tr.dn-row:hover td {
          background-color: #e5e7eb;
        }`}
      </style>
      <div className="list-header" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#333' }}>Client Statements</h2>
      </div>

      <div className="toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>STATEMENT START DATE</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 160 }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 260 }}>
            <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>CUSTOMER</label>
            <Select2Dropdown
              options={customers}
              value={customerId}
              onChange={handleCustomerChange}
              placeholder="All Customers"
              displayKey="text"
              valueKey="id"
              clearable
              menuPlacement="auto"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: '#444', fontWeight: 600 }}>SEARCH (IDF / BL)</label>
            <input
              className="search-input"
              placeholder="Search IDF or BL..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleApplySearch(); }}
              style={{ width: 220 }}
            />
          </div>
          <button className="add-btn" style={{ background: '#007bff' }} onClick={handleApplySearch}>Search</button>
          <button className="add-btn" style={{ background: '#6c757d' }} onClick={handleClear}>Clear</button>
          <button className="add-btn" style={{ background: '#3f6b32' }} onClick={loadAllCounts}>Refresh</button>
        </div>
      </div>

      <div className="custom-tabs-container" style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex' }}>
          {[
            { key: 'released', label: 'Released Jobs', count: pendingRows.filter((r) => r.invoice_no || r.is_active === false).length },
            { key: 'additional', label: 'Additional', count: additionalRows.length },
            { key: 'rejected', label: 'Rejections', count: rejectedRows.length },
            { key: 'stage1', label: 'Stage 1', count: stage1Rows.length },
            { key: 'stage2', label: 'Stage 2', count: stage2Rows.length },
            { key: 'generated', label: 'Statement', count: generatedRows.length },
          ].map((tab) => (
            <button
              key={tab.key}
              className={
                `custom-tab ${activeTab === tab.key ? 'active' : ''}` +
                (tab.key === 'rejected' ? ` cs-rejected-tab${rejectedRows.length > 0 ? ' rejected-tab-alert' : ''}` : '') +
                (tab.key === 'additional' ? ` cs-additional-tab${additionalRows.length > 0 ? ' additional-tab-alert' : ''}` : '') +
                (tab.key === 'generated' ? ' cs-generated-tab' : '')
              }
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="cp-loader">Loading…</div> : (
        <div className="cp-table-wrap">
          {activeTab === 'released' && renderReleasedJobsTable()}
          {activeTab === 'additional' && renderAdditionalTable()}
          {activeTab === 'stage1' && renderStage1Table()}
          {activeTab === 'stage2' && renderStage2Table()}
          {activeTab === 'generated' && (
            <>
              <div style={{ display: 'flex', marginBottom: 8, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex' }}>
                {[
                  { key: 'pending', label: 'Pending', count: generatedRows.filter((r) => !r.email_sent_at).length },
                  { key: 'ledger', label: 'Ledger', count: generatedRows.length },
                ].map((t) => (
                  <button
                    key={t.key}
                    className={`custom-tab ${generatedSubTab === t.key ? 'active' : ''}`}
                    onClick={() => setGeneratedSubTab(t.key)}
                  >
                    {t.label} ({t.count})
                  </button>
                ))}
                </div>
                {generatedSubTab === 'ledger' && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {[
                      { key: 'dr_tzs', label: 'DR BALANCE TZS', value: ledgerTotals.drTzs, bg: '#fee2e2', color: '#b91c1c' },
                      { key: 'cr_tzs', label: 'CR BALANCE TZS', value: ledgerTotals.crTzs, bg: '#dcfce7', color: '#15803d' },
                      { key: 'dr_usd', label: 'DR BALANCE USD', value: ledgerTotals.drUsd, bg: '#fee2e2', color: '#b91c1c' },
                      { key: 'cr_usd', label: 'CR BALANCE USD', value: ledgerTotals.crUsd, bg: '#dcfce7', color: '#15803d' },
                    ].filter((c) => c.value !== 0).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setLedgerFilter((prev) => (prev === c.key ? null : c.key))}
                        title={`Show only ${c.key.startsWith('dr') ? 'positive (DR)' : 'negative (CR)'} ${c.key.endsWith('usd') ? 'USD' : 'TZS'} balances`}
                        style={{
                          background: c.bg,
                          color: c.color,
                          fontWeight: 700,
                          border: ledgerFilter === c.key ? `2px solid ${c.color}` : '1px solid transparent',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.label}: {formatCurrency(c.value)}
                      </button>
                    ))}
                    {[
                      { label: 'FINAL TZS BAL', value: ledgerTotals.totalTzs },
                      { label: 'FINAL USD BAL', value: ledgerTotals.totalUsd },
                    ].filter((c) => c.value !== 0).map((c) => (
                      <div
                        key={c.label}
                        style={{
                          background: '#fef9c3',
                          color: c.value > 0 ? '#b91c1c' : '#15803d',
                          fontWeight: 700,
                          border: '1px solid transparent',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.label}: {formatCurrency(Math.abs(c.value))}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setLedgerFilter(null)}
                      style={{
                        background: '#e2e8f0',
                        color: '#334155',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              {renderGeneratedTable(generatedSubTab === 'pending' ? generatedRows.filter((r) => !r.email_sent_at) : generatedRows)}
            </>
          )}
          {activeTab === 'rejected' && renderRejectionsTable()}
        </div>
      )}

      {runningStmtWindows.map(({ key, customer, shortName }) => (
        <PopoutWindow
          key={key}
          title={`Running Statement - ${shortName || customer}`}
          name={key}
          onClose={() => setRunningStmtWindows((prev) => prev.filter((w) => w.key !== key))}
        >
          <RunningStatement customer={customer} customerShortName={shortName} />
        </PopoutWindow>
      ))}

      {viewStatements.map(({ row, windowId }) => {
        const rejectionReason = rejectionReasons[windowId] || '';
        return (
        <PopoutWindow
          key={windowId}
          title={`Statement - ${row.idf_no || row.id}`}
          name={`statement_view_${row.id}_${windowId}`}
          onClose={() => { closeStatementWindow(windowId); setConfirmDialog((prev) => (prev.windowId === windowId ? { open: false, title: '', message: '' } : prev)); }}
        >
          <div style={{ padding: 16 }}>
            {renderStatementApprovalHistory(row, windowId)}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0 }}>Generate Debit Note</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => openExpensesWindow({ id: row.job, idf_no: row.idf_no || '' })}
                  style={{
                    background: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 14px',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                  }}
                >
                  View Expenses
                </button>
                {((canApprove && !['GENERATED', 'REJECTED'].includes(row.status)) || (row.status === 'GENERATED' && canDelete)) && (
                  <>
                    {canApprove && !['GENERATED', 'REJECTED'].includes(row.status) && (
                      <button
                        onClick={() => promptApprove(row, windowId)}
                        disabled={actionLoading[row.id] || (row.status === 'APPROVED 1' && !canApproveStage2)}
                        title={row.status === 'APPROVED 1' && !canApproveStage2 ? 'Requires Payables Stage 3 permission' : 'Approve'}
                        style={{
                          background: (row.status === 'APPROVED 1' && !canApproveStage2) ? '#94a3b8' : '#16a34a',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '6px 14px',
                          fontSize: '0.85rem',
                          cursor: (actionLoading[row.id] || (row.status === 'APPROVED 1' && !canApproveStage2)) ? 'not-allowed' : 'pointer',
                          opacity: (actionLoading[row.id] || (row.status === 'APPROVED 1' && !canApproveStage2)) ? 0.6 : 1,
                        }}
                      >
                        {actionLoading[row.id] ? '…' : 'Approve'}
                      </button>
                    )}
                    {((canApprove && row.status !== 'GENERATED') || (row.status === 'GENERATED' && canDelete)) && (
                      <button
                        onClick={() => promptReject(row, windowId)}
                        disabled={actionLoading[row.id] || !rejectionReason.trim()}
                        style={{
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          padding: '6px 14px',
                          fontSize: '0.85rem',
                          cursor: (actionLoading[row.id] || !rejectionReason.trim()) ? 'not-allowed' : 'pointer',
                          opacity: (actionLoading[row.id] || !rejectionReason.trim()) ? 0.6 : 1,
                        }}
                      >
                        {actionLoading[row.id] ? '…' : 'Reject'}
                      </button>
                    )}
                    {((canApprove && row.status !== 'GENERATED') || (row.status === 'GENERATED' && canDelete)) && (
                      <input
                        type="text"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReasons((prev) => ({ ...prev, [windowId]: e.target.value }))}
                        placeholder="Rejection reason..."
                        style={{ width: 220, padding: 6, borderRadius: 4, border: '1px solid #cbd5e1' }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            <StatementTopFields data={row} />
            <table className="cp-table" style={{ borderCollapse: 'collapse', width: '100%', marginBottom: 20 }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Charge Name</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amount (TZS)</th>
                  {(row.items || []).some((it) => it.currency === 'USD') && (
                    <th style={{ ...headerCellStyle, textAlign: 'right' }}>Amount (USD)</th>
                  )}
                  <th style={{ ...headerCellStyle, textAlign: 'center' }}>Round Off</th>
                  <th style={{ ...headerCellStyle, textAlign: 'right' }}>Statement Amount</th>
                </tr>
              </thead>
              <tbody>
                {(row.items || []).map((it, i) => (
                  <tr key={i}>
                    <td style={{ ...cellStyle, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {it.statement_charge_name && (
                        <div style={{ fontWeight: 500 }}>{it.statement_charge_name}</div>
                      )}
                      <div style={{ fontSize: it.statement_charge_name ? '0.85em' : 'inherit', color: it.statement_charge_name ? '#64748b' : 'inherit', marginTop: it.statement_charge_name ? 4 : 0 }}>
                        <span>{it.statement_charge_name ? it.charge_name : toDisplay(it.charge_name)}</span>
                      </div>
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>{it.currency === 'USD' ? '-' : formatCurrency(parseAmount(it.actual_tzs) || it.amount)}</td>
                    {(row.items || []).some((x) => x.currency === 'USD') && (
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{it.currency === 'USD' ? formatCurrency(parseAmount(it.actual_usd) || it.amount) : '-'}</td>
                    )}
                    <td style={{ ...cellStyle, textAlign: 'center' }}>{it.rounded ? 'Yes' : 'No'}</td>
                    {(() => {
                      const amt = it.currency === 'USD'
                        ? (parseAmount(it.actual_usd) || parseAmount(it.amount) || 0)
                        : (parseAmount(it.actual_tzs) || parseAmount(it.amount) || 0);
                      const diff = (parseAmount(it.statement_amount) || 0) - amt;
                      return (
                        <td
                          style={{ ...cellStyle, textAlign: 'right', ...(diff !== 0 ? { backgroundColor: '#fef08a' } : {}) }}
                          title={diff !== 0 ? `Difference: ${formatCurrency(diff)}` : undefined}
                        >
                          {formatCurrency(it.statement_amount)}
                        </td>
                      );
                    })()}
                  </tr>
                ))}
                <tr>
                  <td colSpan={(row.items || []).some((it) => it.currency === 'USD') ? 4 : 3} style={{ ...cellStyle, fontWeight: 'bold', textAlign: 'right' }}>Total</td>
                  <td style={{ ...cellStyle, fontWeight: 'bold', textAlign: 'right' }}>{formatCurrency(row.total_amount)}</td>
                </tr>
              </tbody>
            </table>

            <h3 style={{ margin: '0 0 12px' }}>Statement Preview</h3>
            <StatementPreview
              statementId={row.id}
              fileName={`Statement - ${row.idf_no || row.id}`}
            />
          </div>

          <ConfirmDialog
            open={confirmDialog.open && confirmDialog.windowId === windowId}
            title={confirmDialog.title}
            message={confirmDialog.message}
            okOnly={!!confirmDialog.okOnly}
            onConfirm={handleConfirm}
            onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
          />
        </PopoutWindow>
        );
      })}

      <EmailModal
        isOpen={emailModal.open}
        onClose={() => setEmailModal({ open: false, row: null })}
        onSave={() => {}}
        forceFrom={emailModal.officeEmail || 'accounts@kkcompany.co.tz'}
        subjectBuilder={(template) => {
          const r = emailModal.row;
          const clientName = (r && r.customer_name) || 'Unknown Client';
          const refPart = r && r.client_ref_no ? `REF. NO: ${r.client_ref_no} ` : '';
          // Debit Note number as shown on top of the generated PDF: "042) KK/1231/2026 IDF 9901431-26"
          const dnNo = r && r.statement_no
            ? `${r.statement_no}) ${r.kk_file_no || ''} IDF ${r.idf_no || ''}`.replace(/\s+/g, ' ').trim()
            : '';
          const dnPart = dnNo ? ` - ${dnNo}` : '';
          const idfPart = dnNo ? '' : `IDF: ${(r && r.idf_no) || 'N/A'} - `;
          return `${clientName} - ${refPart}${idfPart}${(template && template.subject) || ''}${dnPart}`;
        }}
        jobData={
          emailModal.row
            ? {
                id: emailModal.row.job,
                customer_name: emailModal.row.customer_name,
                idf_no: emailModal.row.idf_no,
                customer_ref: emailModal.row.client_ref_no,
              }
            : null
        }
        jobAttachments={emailModal.jobAttachments || []}
        defaultTo={emailModal.clientEmail || ''}
        customerEmailGroups={emailModal.emailGroups || []}
        sendEmail={async (data, jobId) => {
          const res = await sendAndSaveStatementEmail(data, jobId, emailModal.statementContext);
          if (res?.success && emailModal.row?.id) {
            const sentAt = new Date().toISOString();
            setGeneratedRows((prev) => prev.map((r) => r.id === emailModal.row.id ? { ...r, email_sent_at: sentAt } : r));
          }
          return res;
        }}
        previewAttachments={true}
        bankOptions={emailModal.statementContext?.balance_display ? emailBankOptions : []}
        bodyAppendHtml={(bank) => statementEmailAppendHtml({ ...emailModal.statementContext, company_name: companyName }, bank?.bank || null)}
      />

      {viewPayableDetails.map(({ type, data, windowId }) => (
        <PopoutWindow
          key={windowId}
          title="View Payable"
          name={`payable_detail_${type}_${data.id || ''}_${windowId}`}
          onClose={() => closePayableDetailWindow(windowId)}
        >
          <div className="dialog-body" style={{ padding: 20, background: 'white', border: '1px solid #ddd', fontFamily: 'Arial, sans-serif', fontSize: '8px', overflow: 'auto', height: '100vh', boxSizing: 'border-box' }}>
            <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>View Payable</h2>
              </div>
              <button className="btn btn-secondary" onClick={() => closePayableDetailWindow(windowId)}>
                <i className="fa fa-arrow-left" style={{ marginRight: '6px' }}></i> Back
              </button>
            </div>
            {type === 'shipping_line' ? (
              <ShippingLinePayablesForm
                mode="view"
                initialData={data}
                onClose={() => closePayableDetailWindow(windowId)}
                onSave={() => closePayableDetailWindow(windowId)}
                showViewExpenses={false}
                module="SD"
                popoutMode={true}
              />
            ) : (
              <OtherPayablesForm
                mode="view"
                initialData={data}
                onClose={() => closePayableDetailWindow(windowId)}
                onSave={() => closePayableDetailWindow(windowId)}
                showViewExpenses={false}
                module={data.module || 'PERMIT'}
                popoutMode={true}
              />
            )}
          </div>
        </PopoutWindow>
      ))}

      {generateWindows.map((entry) => (
        <PopoutWindow
          key={entry.windowId}
          title={`${entry.editRow ? 'Edit Statement' : 'Generate Debit Note'} - ${entry.job.idf_no || entry.job.id}`}
          name={generateWindowName(entry)}
          onClose={() => closeGenerateWindow(entry.windowId)}
        >
          <GenerateStatementForm
            job={entry.job}
            editRow={entry.editRow}
            excludeStatementId={entry.excludeStatementId}
            canDelete={canDelete}
            onSaved={() => { closeGenerateWindow(entry.windowId); loadAllCounts(); }}
            onOpenExpenses={openExpensesWindow}
          />
        </PopoutWindow>
      ))}

      {viewExpensesList.map(({ id, idf_no, windowId }) => (
        <PopoutWindow
          key={windowId}
          title={`View Expenses - ${idf_no || id}`}
          name={`payable_expenses_${id}_${windowId}`}
          onClose={() => closeExpensesWindow(windowId)}
        >
          <PayableExpensesView
            idfNo={idf_no || ''}
            jobId={id}
            fromInvoicing={false}
            includeInvoiceCharges={true}
            onClose={() => closeExpensesWindow(windowId)}
          />
        </PopoutWindow>
      ))}

      {custStatements.map(({ name, windowId, preset, from, to, url, loading }) => (
        <PopoutWindow
          key={windowId}
          title={`Customer Statement - ${name}`}
          name={`customer_statement_${name.replace(/\W+/g, '_')}_${windowId}`}
          onClose={() => closeCustomerStatement(windowId)}
          toastContainerId={`cust-stmt-toast-${windowId}`}
        >
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', minHeight: '100vh', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                {stmtRangePresets.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyCustStmtPreset(windowId, name, p.id)}
                    disabled={loading}
                    style={{
                      padding: '4px 12px', fontSize: 12,
                      fontWeight: (preset || 'range') === p.id ? 'bold' : 'normal',
                      border: (preset || 'range') === p.id ? '2px solid #1f4e79' : '1px solid #bbb',
                      borderRadius: 4, background: (preset || 'range') === p.id ? '#dbeafe' : '#f5f5f5', cursor: 'pointer',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
                {preset === 'range' && (
                  <>
                    <input
                      type="date"
                      className="search-input"
                      style={{ width: 115 }}
                      value={from}
                      onChange={(e) => updateCustStatement(windowId, { from: e.target.value })}
                    />
                    <span style={{ fontSize: 12 }}>to</span>
                    <input
                      type="date"
                      className="search-input"
                      style={{ width: 115 }}
                      value={to}
                      onChange={(e) => updateCustStatement(windowId, { to: e.target.value })}
                    />
                    <button
                      className="add-btn"
                      style={{ background: '#2563eb' }}
                      onClick={() => generateCustStatement(windowId, name, from, to)}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Preview Statement'}
                    </button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
                {loading && <span style={{ fontSize: 12, color: '#64748b' }}>Loading...</span>}
                <button
                  className="add-btn"
                  style={{ background: '#dc2626' }}
                  onClick={() => downloadCustStatement(windowId, 'pdf')}
                >
                  Download PDF
                </button>
                <button
                  className="add-btn"
                  style={{ background: '#16a34a' }}
                  onClick={() => downloadCustStatement(windowId, 'excel')}
                >
                  Download Excel
                </button>
              </div>
            </div>
            <div style={{ flex: 1, border: '1px solid #cbd5e1', background: '#fff', minHeight: 500 }}>
              {url ? (
                <iframe
                  src={url}
                  title="Customer Statement Preview"
                  style={{ width: '100%', height: '78vh', border: 'none' }}
                />
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
                  {loading ? 'Generating statement…' : 'Select a date range and click Generate Statement.'}
                </div>
              )}
            </div>
          </div>
        </PopoutWindow>
      ))}

      {adminStatements.map(({ name, windowId, preset, from, to, loading, url }) => (
        <PopoutWindow
          key={windowId}
          title={`Admin Statement - ${name}`}
          name={`admin_statement_${name.replace(/\W+/g, '_')}_${windowId}`}
          onClose={() => closeAdminStatement(windowId)}
        >
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', minHeight: '100vh', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                {stmtRangePresets.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyAdminStmtPreset(windowId, name, p.id)}
                    disabled={loading}
                    style={{
                      padding: '4px 12px', fontSize: 12,
                      fontWeight: (preset || 'range') === p.id ? 'bold' : 'normal',
                      border: (preset || 'range') === p.id ? '2px solid #1f4e79' : '1px solid #bbb',
                      borderRadius: 4, background: (preset || 'range') === p.id ? '#dbeafe' : '#f5f5f5', cursor: 'pointer',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
                {preset === 'range' && (
                  <>
                    <input
                      type="date"
                      className="search-input"
                      style={{ width: 115 }}
                      value={from}
                      onChange={(e) => setAdminStatements((prev) => prev.map((x) => (x.windowId === windowId ? { ...x, from: e.target.value } : x)))}
                    />
                    <span style={{ fontSize: 12 }}>to</span>
                    <input
                      type="date"
                      className="search-input"
                      style={{ width: 115 }}
                      value={to}
                      onChange={(e) => setAdminStatements((prev) => prev.map((x) => (x.windowId === windowId ? { ...x, to: e.target.value } : x)))}
                    />
                    <button
                      className="add-btn"
                      style={{ background: '#2563eb' }}
                      onClick={() => generateAdminStatement(windowId, name, from, to)}
                      disabled={loading}
                    >
                      {loading ? 'Loading...' : 'Preview Statement'}
                    </button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', marginTop: 10 }}>
                {loading && <span style={{ fontSize: 12, color: '#64748b' }}>Loading...</span>}
                <button
                  className="add-btn"
                  style={{ background: '#dc2626' }}
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API_BASE}/customer-statement-admin-pdf/?${custStmtParams(name, from, to)}`, { headers: getAuthHeaders(false) });
                      if (!res.ok) throw new Error('Download failed');
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.download = `Admin_Statement_${(name || 'customer').replace(/[^\w]+/g, '_')}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                    } catch (e) { toast.error(e.message || 'Download failed'); }
                  }}
                >
                  Download PDF
                </button>
              </div>
            </div>
            <div style={{ flex: 1, border: '1px solid #cbd5e1', background: '#fff', minHeight: 500 }}>
              {url ? (
                <iframe
                  src={url}
                  title="Admin Statement Preview"
                  style={{ width: '100%', height: '78vh', border: 'none' }}
                />
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
                  {loading ? 'Generating admin statement…' : 'Failed to load admin statement.'}
                </div>
              )}
            </div>
          </div>
        </PopoutWindow>
      ))}

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Debit Note"
        message={deleteConfirm.row ? `Are you sure you would like to delete debit note ${deleteConfirm.row.statement_no || deleteConfirm.row.id}?` : ''}
        onConfirm={handleDeleteStatement}
        onCancel={() => setDeleteConfirm({ open: false, row: null })}
      />
    </div>
  );
};

export default ClientStatements;
